import type {
  GatewayStatus,
  ManualReviewItem,
  MemberRecord,
  OrderDetail,
  OrderItemRecord,
  PaymentMethod,
  PaymentStatus,
  ProductSnapshot,
  ServiceRequestRecord,
  TableSnapshot,
  VersionInfo,
} from "@billiard/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRightLeft,
  BadgeAlert,
  Banknote,
  BarChart3,
  BellRing,
  Check,
  ChevronRight,
  CircleDollarSign,
  CircleHelp,
  Clock3,
  Database,
  Download,
  FileCheck2,
  FileUp,
  HardDrive,
  LayoutGrid,
  ListChecks,
  LockKeyhole,
  LogIn,
  LogOut,
  Minus,
  PackagePlus,
  Power,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  Search,
  ServerCog,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  UserRoundCheck,
  UsersRound,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { lazy, type ReactNode, Suspense, useEffect, useId, useRef, useState } from "react";
import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import {
  ApiRequestError,
  acknowledgeServiceRequest,
  addOrderItem,
  adjustSessionStart,
  checkoutSession,
  clearTable,
  completeServiceRequest,
  endSessionEarly,
  getConnectivity,
  getDeviceAdapterStatus,
  getDiagnosticBundle,
  getOperatorSession,
  getOrder,
  getOrdersCsv,
  getOwnerBusinessSummary,
  getOwnerReconciliationSummary,
  getProductCatalogTemplate,
  getPublicVersion,
  getReconciliationSummary,
  getSystemStatus,
  importProductCatalog,
  listInventoryProducts,
  listManualReview,
  listMembers,
  listOrders,
  listProducts,
  listServiceRequests,
  listTables,
  loginOperator,
  logoutOperator,
  type OperatorIdentity,
  type OperatorSessionContext,
  type OwnerBusinessSummary,
  openSession,
  pauseSession,
  previewProductCatalog,
  reconcilePayment,
  refundOrder,
  reprintReceipt,
  requestSecondaryAuthorization,
  resolveCoachConflict,
  resolveLongSession,
  resolveOrderConflict,
  resumeSession,
  returnOrderItem,
  setOperatorRequestIdentity,
  simulateNetwork,
  transferSession,
  voidSession,
} from "./api";
import {
  AutofillSafeInput,
  clearSensitiveDraftCredential,
  loginCredentialAttributes,
  takeSensitiveCredential,
} from "./autofillGuard";
import { runIdempotentOperation } from "./idempotency";
import { NativeRuntimeNotice } from "./NativeRuntimeNotice";
import { isNativeRuntime, listenForNativeResume, nativeAppInfo } from "./nativeRuntime";
import type { NativeSessionMode } from "./nativeSession";
import {
  isOperatorPasswordValid,
  OPERATOR_PASSWORD_MAX_LENGTH,
  OPERATOR_PASSWORD_MIN_LENGTH,
} from "./operatorPassword";

const CashShiftDesk = lazy(() =>
  import("./CashShiftDesk").then((module) => ({ default: module.CashShiftDesk })),
);
const CoachDesk = lazy(() =>
  import("./CoachDesk").then((module) => ({ default: module.CoachDesk })),
);
const ProductsDesk = lazy(() =>
  import("./CommercialDesk").then((module) => ({ default: module.ProductsDesk })),
);
const MembersDesk = lazy(() =>
  import("./CommercialDesk").then((module) => ({ default: module.MembersDesk })),
);
const GroupBuyDesk = lazy(() =>
  import("./GroupBuyDesk").then((module) => ({ default: module.GroupBuyDesk })),
);
const OperatingConfigurationDesk = lazy(() =>
  import("./OperatingConfigurationDesk").then((module) => ({
    default: module.OperatingConfigurationDesk,
  })),
);
const StaffManagementDesk = lazy(() =>
  import("./StaffManagementDesk").then((module) => ({ default: module.StaffManagementDesk })),
);

type OperatorRole = OperatorIdentity["role"];

const allOperatorRoles: readonly OperatorRole[] = [
  "CASHIER",
  "MANAGER",
  "OWNER",
  "WAITER",
  "COACH",
];

const roleLabels: Record<OperatorRole, string> = {
  CASHIER: "前台收银员",
  MANAGER: "店长",
  OWNER: "老板",
  WAITER: "服务员",
  COACH: "助教",
};

export const routePermissions: Record<string, readonly OperatorRole[]> = {
  "/frontdesk": ["CASHIER", "MANAGER", "OWNER"],
  "/shifts": ["CASHIER", "MANAGER", "OWNER"],
  "/coach": ["CASHIER", "MANAGER", "OWNER", "WAITER", "COACH"],
  "/tasks": ["CASHIER", "MANAGER", "OWNER", "WAITER"],
  "/orders": ["CASHIER", "MANAGER", "OWNER"],
  "/review": ["CASHIER", "MANAGER", "OWNER"],
  "/products": ["MANAGER", "OWNER"],
  "/group-buy": ["CASHIER", "MANAGER", "OWNER"],
  "/members": ["CASHIER", "MANAGER", "OWNER"],
  "/staff": ["MANAGER", "WAITER"],
  "/owner": ["MANAGER", "OWNER"],
  "/configuration": ["MANAGER", "OWNER"],
  "/system": ["MANAGER", "OWNER"],
  "/help": allOperatorRoles,
};

export function canAccessRoute(role: OperatorRole, path: string): boolean {
  return routePermissions[path]?.includes(role) ?? false;
}

export function defaultRouteForRole(role: OperatorRole): string {
  if (role === "COACH") return "/coach";
  if (role === "WAITER") return "/staff";
  return "/frontdesk";
}

export function sessionLandingRoute(
  role: OperatorRole,
  session: OperatorSessionContext | undefined,
): string {
  if (session?.mode !== "SHARED_SHIFT") return defaultRouteForRole(role);
  return session.shiftStatus === "OPEN" ? "/frontdesk" : "/shifts";
}

export function sessionCanOpenRoute(
  session: OperatorSessionContext | undefined,
  path: string,
): boolean {
  if (session?.mode !== "SHARED_SHIFT" || session.shiftStatus === "OPEN") return true;
  return path === "/shifts" || path === "/help";
}

export function operatorSessionEvidenceLabel(evidence: "SIMULATED" | "STORE_ACCEPTED"): string {
  return evidence === "SIMULATED" ? "演示账号" : "账号已启用";
}

export function isMemberPaymentAmountValid({
  selectedMember,
  orderTotalFen,
  memberOnly,
  useMixedBalance,
  mixedBalanceFen,
}: {
  selectedMember: Pick<MemberRecord, "id" | "availableBalanceFen"> | undefined;
  orderTotalFen: number | undefined;
  memberOnly: boolean;
  useMixedBalance: boolean;
  mixedBalanceFen: number;
}): boolean {
  if (memberOnly) {
    return Boolean(selectedMember && orderTotalFen !== undefined && orderTotalFen > 0);
  }
  if (!useMixedBalance) return true;
  return Boolean(
    selectedMember &&
      orderTotalFen !== undefined &&
      Number.isFinite(mixedBalanceFen) &&
      mixedBalanceFen > 0 &&
      mixedBalanceFen < orderTotalFen,
  );
}

export interface MemberCheckoutPlan {
  walletAmountFen: number;
  externalAmountFen: number;
  externalLabel: string;
}

export function memberCheckoutPlan({
  method,
  orderTotalFen,
  memberOnly,
  useMixedBalance,
  mixedBalanceFen,
}: {
  method: PaymentMethod;
  orderTotalFen: number | undefined;
  memberOnly: boolean;
  useMixedBalance: boolean;
  mixedBalanceFen: number;
}): MemberCheckoutPlan | null {
  if (orderTotalFen === undefined || orderTotalFen <= 0 || (!memberOnly && !useMixedBalance)) {
    return null;
  }
  const walletAmountFen = memberOnly ? orderTotalFen : mixedBalanceFen;
  if (walletAmountFen <= 0 || walletAmountFen > orderTotalFen) return null;
  return {
    walletAmountFen,
    externalAmountFen: orderTotalFen - walletAmountFen,
    externalLabel:
      method === "CASH"
        ? "现金应收"
        : method === "STATIC_WECHAT"
          ? "微信静态码待确认"
          : method === "STATIC_ALIPAY"
            ? "支付宝静态码待确认"
            : "外部收款",
  };
}

export function CheckoutPaymentPlan({ plan }: { plan: MemberCheckoutPlan }): React.JSX.Element {
  return (
    <section className="checkout-payment-plan" aria-label="本次收款计划" aria-live="polite">
      <div>
        <span>计划钱包抵扣</span>
        <strong>{formatFee(plan.walletAmountFen)}</strong>
      </div>
      <div>
        <span>{plan.externalAmountFen > 0 ? plan.externalLabel : "外部应收"}</span>
        <strong>{formatFee(plan.externalAmountFen)}</strong>
      </div>
      <p>
        中心账本必须足额确认上述钱包抵扣，系统才会结账；余额不足时不会自动增加现金或静态码金额。
      </p>
    </section>
  );
}

function rolesFor(path: string): readonly OperatorRole[] {
  return routePermissions[path] ?? [];
}

const navigationSections = [
  {
    id: "operations",
    label: "日常营业",
    items: [
      { to: "/frontdesk", label: "前台", icon: LayoutGrid, roles: rolesFor("/frontdesk") },
      { to: "/shifts", label: "交接班", icon: Clock3, roles: rolesFor("/shifts") },
      { to: "/coach", label: "助教", icon: UsersRound, roles: rolesFor("/coach") },
      { to: "/tasks", label: "待办", icon: BellRing, roles: rolesFor("/tasks") },
      { to: "/orders", label: "订单", icon: ReceiptText, roles: rolesFor("/orders") },
      { to: "/review", label: "待核对", icon: BadgeAlert, roles: rolesFor("/review") },
    ],
  },
  {
    id: "customers",
    label: "会员与商品",
    items: [
      { to: "/products", label: "商品库存", icon: PackagePlus, roles: rolesFor("/products") },
      { to: "/group-buy", label: "团购", icon: FileCheck2, roles: rolesFor("/group-buy") },
      { to: "/members", label: "会员", icon: CircleDollarSign, roles: rolesFor("/members") },
    ],
  },
  {
    id: "management",
    label: "团队与经营",
    items: [
      { to: "/staff", label: "服务员", icon: UserRoundCheck, roles: rolesFor("/staff") },
      { to: "/owner", label: "老板", icon: BarChart3, roles: rolesFor("/owner") },
    ],
  },
  {
    id: "venue",
    label: "门店管理",
    items: [
      {
        to: "/configuration",
        label: "门店设置",
        icon: SlidersHorizontal,
        roles: rolesFor("/configuration"),
      },
      { to: "/system", label: "系统", icon: Settings, roles: rolesFor("/system") },
      { to: "/help", label: "说明", icon: CircleHelp, roles: rolesFor("/help") },
    ],
  },
];

export function navigationSectionsForRole(role: OperatorRole) {
  return navigationSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item.roles.includes(role)),
    }))
    .filter((section) => section.items.length > 0);
}

const paymentLabels: Record<PaymentMethod, string> = {
  CASH: "现金",
  STATIC_WECHAT: "微信静态码",
  STATIC_ALIPAY: "支付宝静态码",
  MEMBER_BALANCE: "会员余额",
};

const paymentStatusLabels: Record<PaymentStatus, string> = {
  CONFIRMED_LOCAL: "现金本地确认",
  CONFIRMED_CLOUD: "会员余额云端确认",
  PENDING_RECONCILIATION: "待渠道核对",
  MANUALLY_RECONCILED: "人工核对一致",
  RECONCILIATION_EXCEPTION: "核对异常",
  PARTIALLY_REFUNDED: "部分退款",
  REFUNDED: "已退款",
};

const orderStatusLabels: Record<OrderDetail["status"], string> = {
  OPEN: "进行中",
  SETTLED: "已结账",
  REFUNDED: "已退款",
  CONFLICT: "同步冲突",
};

const syncStateLabels: Record<OrderDetail["syncState"], string> = {
  PENDING: "待同步",
  SYNCED: "已同步",
  CONFLICT: "同步冲突",
  FAILED: "同步失败",
  ISOLATED: "已隔离（未写入云端）",
};

const reviewReasonLabels: Record<string, string> = {
  PENDING_CHANNEL_CONFIRMATION: "待渠道核对",
  MANUALLY_RECONCILED: "人工核对一致",
  NOT_RECEIVED: "未到账",
  AMOUNT_MISMATCH: "金额不符",
  DUPLICATE_PAYMENT: "重复付款",
  AGGREGATE_VERSION_CONFLICT: "订单版本冲突",
  IDEMPOTENCY_COLLISION: "同一次操作收到不一致的重复请求",
  ESCALATED_FOR_LEDGER_REVIEW: "已隔离并转交账务复核",
  ISOLATION_CONFIRMED: "已确认保留隔离",
  ESCALATED_FOR_OPERATIONS_REVIEW: "已转交门店运营复核",
  COACH_SYNC_PERMANENTLY_ISOLATED: "助教同步事项已永久隔离",
};

function reviewReasonLabel(item: ManualReviewItem): string {
  if (item.type === "COACH_CONFLICT" && item.reasonCode === "AGGREGATE_VERSION_CONFLICT") {
    return "助教记录版本冲突";
  }
  return reviewReasonLabels[item.reasonCode] ?? "需要人工判断";
}

const stageLabels: Record<GatewayStatus["syncStage"], string> = {
  IDLE: "准备同步",
  UPLOADING_OFFLINE_ORDERS: "上传离线订单",
  RECONCILING_PAYMENTS: "核对收款",
  PULLING_CONFIGURATION: "拉取配置",
  CHECKING_CONFLICTS: "检查冲突",
};

function formatFee(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

export function OrderBreakdown({
  order,
}: {
  order: Pick<
    OrderDetail,
    | "tableFeeFen"
    | "productTotalFen"
    | "coachServiceTotalFen"
    | "groupBuyFulfillments"
    | "refundedFen"
  >;
}): React.JSX.Element {
  return (
    <div className="order-breakdown">
      <span>台费 {formatFee(order.tableFeeFen)}</span>
      <span>商品 {formatFee(order.productTotalFen)}</span>
      <span>助教服务 {formatFee(order.coachServiceTotalFen)}</span>
      {(order.groupBuyFulfillments ?? []).map((fulfillment) => (
        <span key={fulfillment.id}>
          {fulfillment.channelSnapshot.provider === "MEITUAN" ? "美团" : "抖音"}券覆盖{" "}
          {formatFee(fulfillment.voucherCoveredFen)} · 到店补差{" "}
          {formatFee(fulfillment.customerSupplementFen)}
        </span>
      ))}
      <span>已退 {formatFee(order.refundedFen)}</span>
    </div>
  );
}

function formatTime(value: string | null): string {
  if (!value) return "尚无成功记录";
  return new Date(value).toLocaleString("zh-CN", { hour12: false });
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  const remaining = seconds % 60;
  return [hours, minutes, remaining].map((value) => String(value).padStart(2, "0")).join(":");
}

function hasFreshConnectivity(query: {
  data: GatewayStatus | undefined;
  dataUpdatedAt: number;
  failureCount: number;
  isError: boolean;
}): boolean {
  return (
    Boolean(query.data) &&
    !query.isError &&
    query.failureCount === 0 &&
    Date.now() - query.dataUpdatedAt <= 3_000
  );
}

export function canResolveOrderConflict(status: GatewayStatus | undefined): boolean {
  return Boolean(
    status &&
      status.offlineAuthorityAvailable &&
      (status.mode === "ONLINE" ||
        (status.mode === "RECOVERING" && status.syncStage === "CHECKING_CONFLICTS")),
  );
}

export function connectivityRecoveryPresentation(input: {
  failedRecords: number;
  syncStage: GatewayStatus["syncStage"];
}): { blocked: boolean; title: string; detail: string } {
  if (input.failedRecords > 0) {
    return {
      blocked: true,
      title: "业务同步已暂停，等待人工处理",
      detail: `云端网络可达，但有 ${input.failedRecords} 条经营记录连续同步失败；本地原始记录已保留，请由老板或店长到“待人工确认”处理。`,
    };
  }

  return {
    blocked: false,
    title: "网络已恢复，正在同步和核对",
    detail: `当前阶段：${stageLabels[input.syncStage]}`,
  };
}

function useDuration(startedAt: string | null): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [startedAt]);
  if (!startedAt) return "00:00:00";
  return formatDuration(Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1_000)));
}

function CspModal({
  open,
  onOpenChange,
  labelledBy,
  describedBy,
  className,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  labelledBy: string;
  describedBy: string;
  className: string;
  children: ReactNode;
}): React.JSX.Element {
  const modalRef = useRef<HTMLDialogElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal || !open) return;

    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!modal.open) {
      if (typeof modal.showModal === "function") modal.showModal();
      else modal.setAttribute("open", "");
    }
    document.body.classList.add("dialog-scroll-locked");

    return () => {
      if (modal.open) modal.close();
      if (!document.querySelector("dialog.csp-dialog[open]")) {
        document.body.classList.remove("dialog-scroll-locked");
      }
      returnFocusRef.current?.focus({ preventScroll: true });
      returnFocusRef.current = null;
    };
  }, [open]);

  return (
    <dialog
      ref={modalRef}
      className={`csp-dialog dialog-content ${className}`}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      onCancel={(event) => {
        event.preventDefault();
        onOpenChange(false);
      }}
      onKeyDown={(event) => {
        // Chromium normally emits `cancel` for Escape on a modal dialog, but
        // keyboard automation and some embedded browser shells only deliver
        // the key event to the focused control. Keep the close behavior
        // deterministic without changing the CSP or focus boundary.
        if (event.key === "Escape") {
          event.preventDefault();
          onOpenChange(false);
        }
      }}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onOpenChange(false);
      }}
    >
      {children}
    </dialog>
  );
}

function LoginScreen({ onAuthenticated }: { onAuthenticated: () => void }): React.JSX.Element {
  const titleId = useId();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const native = isNativeRuntime();
  const [sessionMode, setSessionMode] = useState<NativeSessionMode>("PERSONAL");
  const authentication = useMutation({
    mutationFn: () => loginOperator(login, password, native ? sessionMode : "PERSONAL"),
    onSuccess: () => {
      setPassword("");
      onAuthenticated();
    },
  });
  return (
    <main className="auth-screen">
      <section className="auth-panel" aria-labelledby={titleId}>
        <div className="auth-brand">
          <span className="brand-number">14</span>
          <div>
            <strong>北岸桌球</strong>
            <span>门店经营系统</span>
          </div>
        </div>
        <div className="auth-heading">
          <LockKeyhole size={22} aria-hidden="true" />
          <div>
            <h1 id={titleId}>员工登录</h1>
            <p>使用门店分配的员工账号</p>
          </div>
        </div>
        <form
          name="operator-login"
          autoComplete="on"
          onSubmit={(event) => {
            event.preventDefault();
            authentication.mutate();
          }}
        >
          <label>
            登录名
            <input
              {...loginCredentialAttributes("username")}
              value={login}
              onChange={(event) => setLogin(event.target.value)}
              minLength={3}
              maxLength={64}
              required
            />
          </label>
          {native ? (
            <NativeSessionModePicker value={sessionMode} onChange={setSessionMode} />
          ) : null}
          <label>
            密码
            <input
              type="password"
              {...loginCredentialAttributes("password")}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={OPERATOR_PASSWORD_MIN_LENGTH}
              maxLength={OPERATOR_PASSWORD_MAX_LENGTH}
              required
            />
          </label>
          {authentication.error instanceof Error ? (
            <div className="error-band" role="alert">
              登录失败：{authentication.error.message}
            </div>
          ) : null}
          <button
            className="button primary auth-submit"
            type="submit"
            disabled={
              authentication.isPending || login.length < 3 || !isOperatorPasswordValid(password)
            }
          >
            <LogIn size={18} aria-hidden="true" />
            {authentication.isPending ? "正在登录" : "登录"}
          </button>
        </form>
      </section>
    </main>
  );
}

export function NativeSessionModePicker({
  value,
  onChange,
}: {
  value: NativeSessionMode;
  onChange: (mode: NativeSessionMode) => void;
}): React.JSX.Element {
  return (
    <fieldset className="native-session-mode">
      <legend>这台设备怎么使用？</legend>
      <label>
        <input
          type="radio"
          name="native-session-mode"
          value="PERSONAL"
          checked={value === "PERSONAL"}
          onChange={() => onChange("PERSONAL")}
        />
        <span>
          <strong>我的私人手机</strong>
          <small>安全保存可撤销会话，下次打开 App 可继续使用。</small>
        </span>
      </label>
      <label>
        <input
          type="radio"
          name="native-session-mode"
          value="SHARED_SHIFT"
          checked={value === "SHARED_SHIFT"}
          onChange={() => onChange("SHARED_SHIFT")}
        />
        <span>
          <strong>公共收银设备</strong>
          <small>必须开始班次；交班后自动退出，关闭 App 后也要重新登录。</small>
        </span>
      </label>
    </fieldset>
  );
}

export interface WebBuildProvenance {
  productVersion: string;
  gitCommit: string;
  buildTime: string;
  deploymentEnvironment: string;
}

export const webBuildProvenance: WebBuildProvenance = {
  productVersion: import.meta.env.VITE_APP_VERSION ?? "0.8.49",
  gitCommit: import.meta.env.VITE_GIT_COMMIT ?? "working-tree",
  buildTime: import.meta.env.VITE_BUILD_TIME ?? "unknown",
  deploymentEnvironment: import.meta.env.VITE_DEPLOYMENT_ENVIRONMENT ?? "local-demo",
};

function shortCommit(commit: string): string {
  return commit.length > 12 ? commit.slice(0, 12) : commit;
}

export function buildProvenanceMatches(
  web: WebBuildProvenance,
  gateway: Pick<VersionInfo, "productVersion" | "gitCommit">,
): boolean {
  return web.productVersion === gateway.productVersion && web.gitCommit === gateway.gitCommit;
}

export function BuildProvenanceStamp({
  gateway,
  loading,
  unavailable,
  web = webBuildProvenance,
}: {
  gateway: VersionInfo | undefined;
  loading: boolean;
  unavailable: boolean;
  web?: WebBuildProvenance;
}): React.JSX.Element {
  const matches = gateway ? buildProvenanceMatches(web, gateway) : false;
  return (
    <div
      className="auth-version"
      aria-live="polite"
      data-version-match={gateway ? matches : "unknown"}
    >
      <span>
        网页 v{web.productVersion} · {shortCommit(web.gitCommit)} · {web.deploymentEnvironment}
      </span>
      <span>构建时间 {web.buildTime}</span>
      {loading ? <span>正在核对服务版本…</span> : null}
      {unavailable ? <span className="auth-version-warning">服务版本暂时无法核对</span> : null}
      {gateway ? (
        <span className={matches ? undefined : "auth-version-warning"}>
          网关 v{gateway.productVersion} · {shortCommit(gateway.gitCommit)} · schema{" "}
          {gateway.schemaVersion} · {gateway.deploymentEnvironment}
          {matches ? " · 版本一致" : " · 页面与服务版本不一致"}
        </span>
      ) : null}
    </div>
  );
}

const pageLabels: Record<string, string> = {
  "/frontdesk": "前台营业台",
  "/shifts": "交接班",
  "/coach": "助教工作台",
  "/tasks": "服务待办总览",
  "/orders": "订单中心",
  "/review": "待人工核对",
  "/products": "商品与库存管理",
  "/group-buy": "团购履约与核对",
  "/members": "会员与储值管理",
  "/staff": "服务员工作台",
  "/owner": "老板经营看板",
  "/configuration": "门店经营设置",
  "/system": "系统管理",
  "/help": "使用说明",
};

export function accessDeniedPageLabel(path: string): string {
  return pageLabels[path] ?? "这个页面";
}

function AccessDenied({
  operator,
  path,
}: {
  operator: OperatorIdentity;
  path: string;
}): React.JSX.Element {
  const fallback = defaultRouteForRole(operator.role);
  return (
    <section className="access-denied" role="alert">
      <LockKeyhole size={28} aria-hidden="true" />
      <div>
        <span>权限已拦截</span>
        <h1>当前账号不能进入{accessDeniedPageLabel(path)}</h1>
        <p>
          你当前以“{roleLabels[operator.role]}
          ”身份登录。系统没有加载该页面的数据；如需访问，请由老板或店长分配正确角色。
        </p>
      </div>
      <NavLink className="button primary" to={fallback}>
        返回我的工作台
      </NavLink>
      <NavLink className="button secondary" to="/help">
        查看权限说明
      </NavLink>
    </section>
  );
}

export function AuthorizedRoute({
  operator,
  path,
  children,
}: {
  operator: OperatorIdentity;
  path: string;
  children: React.JSX.Element;
}): React.JSX.Element {
  return canAccessRoute(operator.role, path) ? (
    children
  ) : (
    <AccessDenied operator={operator} path={path} />
  );
}

function DeferredRoute({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <Suspense
      fallback={
        <section className="loading-state" aria-live="polite" aria-busy="true">
          <RefreshCw className="spin" size={20} aria-hidden="true" />
          正在打开工作页面…
        </section>
      }
    >
      {children}
    </Suspense>
  );
}

const helpRows = [
  { path: "/frontdesk", purpose: "开台、计时、转台、商品加退单、结账和清台" },
  { path: "/shifts", purpose: "开始当班、清点现金、提交交班并由下一班确认" },
  { path: "/coach", purpose: "助教排钟、服务；助教账号登录后只显示本人工作台" },
  { path: "/tasks", purpose: "查看桌边呼叫服务与结账请求" },
  { path: "/orders", purpose: "查询订单、退单与退款" },
  { path: "/review", purpose: "处理静态码待核对、支付异常与同步冲突" },
  { path: "/products", purpose: "逐条录入商品、采购入库、出库、盘点和低库存处理" },
  { path: "/members", purpose: "会员建档、充值福利、待核对充值和资金流水" },
  { path: "/staff", purpose: "服务员接单、处理并完成桌边待办" },
  { path: "/owner", purpose: "营业日应收、实收、退款、离线单和差异核对" },
  { path: "/configuration", purpose: "门店资料、价格规则、球台和灯控/打印设备配置" },
  { path: "/system", purpose: "员工账号、健康状态、商品导入、订单与诊断导出" },
] as const;

function HelpDesk({ operator }: { operator: OperatorIdentity }): React.JSX.Element {
  const accessibleRows = helpRows.filter((row) => canAccessRoute(operator.role, row.path));
  return (
    <>
      <PageHeader
        eyebrow={`${operator.role} · ${roleLabels[operator.role]}`}
        title="使用说明与入口"
        action={<span className="demo-pill">当前账号权限</span>}
      />
      <section className="help-intro">
        <div>
          <CircleHelp size={24} aria-hidden="true" />
          <div>
            <strong>你当前应该使用：{pageLabels[defaultRouteForRole(operator.role)]}</strong>
            <span>登录后只显示当前角色可用入口；手工输入无权网址也会在加载数据前被拦截。</span>
          </div>
        </div>
        <NavLink className="button primary" to={defaultRouteForRole(operator.role)}>
          进入我的工作台
        </NavLink>
      </section>

      <section className="help-section">
        <div className="section-heading">
          <div>
            <h2>当前账号可用入口</h2>
            <p>这里只列出“{roleLabels[operator.role]}”可以实际进入的页面。</p>
          </div>
        </div>
        <div className="help-route-grid">
          {accessibleRows.map((row) => (
            <NavLink to={row.path} key={row.path}>
              <strong>{pageLabels[row.path]}</strong>
              <span className="help-route-location">在本应用内打开</span>
              <span>{row.purpose}</span>
            </NavLink>
          ))}
        </div>
      </section>

      <section className="help-section">
        <div className="section-heading">
          <div>
            <h2>角色怎么分</h2>
            <p>一个员工一个账号，不共享老板账号；老板和店长负责分配与停用账号。</p>
          </div>
        </div>
        <div className="permission-table-wrap">
          <table className="permission-table">
            <thead>
              <tr>
                <th scope="col">角色</th>
                <th scope="col">主要工作台</th>
                <th scope="col">不能做</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">老板 / 店长</th>
                <td>经营看板、系统、前台与核对；店长可进入服务员工作台，老板通过待办总览监督</td>
                <td>老板账号不能代替服务员接单；不能把模拟数据当成真实支付或现场验收</td>
              </tr>
              <tr>
                <th scope="row">前台收银员</th>
                <td>前台、助教运营、待办、订单、待核对</td>
                <td>看不到老板看板、系统管理和服务员专属接单台</td>
              </tr>
              <tr>
                <th scope="row">服务员</th>
                <td>服务员工作台、待办、助教现场只读信息</td>
                <td>不能收款、退款、改价格，也看不到老板经营数据</td>
              </tr>
              <tr>
                <th scope="row">助教</th>
                <td>同一个 /coach，但只显示本人班次、队列、任务、服务和提成基础</td>
                <td>不能看其他助教敏感资料，不能收款、改提成或人工调队</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="help-section kiosk-help">
        <div>
          <span>独立门店设备</span>
          <h2>桌边运行屏不是老板端，也不是服务员端</h2>
          <p>
            桌边屏由每张球桌的 Kiosk 应用连接该桌的 <code>table-edge-agent</code>
            ，显示桌号、计时、预估台费，并让顾客呼叫服务或请求结账。正式门店使用局域网设备地址，不直接暴露在公网。
          </p>
        </div>
        <div className="kiosk-address-card">
          <strong>当前公网</strong>
          <span>未公开桌边 Kiosk，这是安全边界，不是 /frontdesk 的别名。</span>
          <strong>本地单桌演示</strong>
          <code>pnpm.cmd run dev:slice</code>
          <code>http://127.0.0.1:5174</code>
          <strong>真实门店</strong>
          <span>由每桌设备固定打开门店局域网页面；真实智能屏还需要到店连接和验收。</span>
        </div>
      </section>
      <NativeAppHelpCard />
    </>
  );
}

function NativeAppHelpCard(): React.JSX.Element | null {
  const native = isNativeRuntime();
  const [info, setInfo] = useState<{ version: string; build: string } | null>(null);
  useEffect(() => {
    if (!native) return;
    void nativeAppInfo().then((result) => {
      if (result) setInfo({ version: result.version, build: result.build });
    });
  }, [native]);
  if (!native) return null;
  return (
    <section className="help-section native-app-help">
      <div className="section-heading">
        <div>
          <h2>手机 App 权限与隐私</h2>
          <p>
            当前 App v{info?.version ?? "读取中"}（build {info?.build ?? "-"}）· Android / iOS
            共用同一套角色权限。
          </p>
        </div>
      </div>
      <div className="help-route-grid">
        <article>
          <strong>登录安全</strong>
          <span>
            App 不保存明文密码。私人手机只把可撤销会话安全存入 Keystore /
            Keychain；公共收银设备不保存会话，关闭 App 或交班后都要重新登录。
          </span>
        </article>
        <article>
          <strong>相机权限</strong>
          <span>
            只有你主动点击商品扫码时才调用相机。拒绝权限后仍可手工输入条码，不影响其他经营功能。
          </span>
        </article>
        <article>
          <strong>断网与更新</strong>
          <span>
            断网时顶部会持续提示，并以门店主机页面列出的受限离线能力为准；有新版本时会提示取得经过验证的更新包。
          </span>
        </article>
      </div>
    </section>
  );
}

function Shell({
  operator,
  authenticationEvidence,
  sessionContext,
}: {
  operator: OperatorIdentity;
  authenticationEvidence: "SIMULATED" | "STORE_ACCEPTED";
  sessionContext: OperatorSessionContext | undefined;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigationRef = useRef<HTMLElement>(null);
  const logout = useMutation({
    mutationFn: logoutOperator,
    onSuccess: async () => {
      await queryClient.resetQueries({ queryKey: ["operator-session"] });
    },
  });
  const sharedShiftSession = sessionContext?.mode === "SHARED_SHIFT";
  const defaultRoute = sessionLandingRoute(operator.role, sessionContext);
  const visibleNavigationSections = navigationSectionsForRole(operator.role)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => sessionCanOpenRoute(sessionContext, item.to)),
    }))
    .filter((section) => section.items.length > 0);
  useEffect(() => {
    if (operator.role !== "OWNER" && operator.role !== "MANAGER") return;
    const preload = window.setTimeout(() => {
      void queryClient.prefetchQuery({
        queryKey: ["inventory-products"],
        queryFn: listInventoryProducts,
        staleTime: 30_000,
      });
    }, 0);
    return () => window.clearTimeout(preload);
  }, [operator.role, queryClient]);
  useEffect(() => {
    if (!window.matchMedia("(max-width: 760px)").matches) return;
    const activeItem = navigationRef.current?.querySelector<HTMLElement>(
      `.nav-item[href="${location.pathname}"]`,
    );
    if (typeof activeItem?.scrollIntoView === "function") {
      activeItem.scrollIntoView({ behavior: "auto", block: "nearest", inline: "center" });
    }
  }, [location.pathname]);
  if (sharedShiftSession && !sessionCanOpenRoute(sessionContext, location.pathname)) {
    return <Navigate to="/shifts" replace />;
  }
  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand-mark" role="img" aria-label="北岸桌球">
          <span className="brand-number">14</span>
          <span className="brand-name">北岸桌球</span>
        </div>
        <nav aria-label="主导航" ref={navigationRef}>
          {visibleNavigationSections.map((section) => (
            <section
              className="nav-section"
              aria-labelledby={`nav-section-${section.id}`}
              key={section.id}
            >
              <span className="nav-section-label" id={`nav-section-${section.id}`}>
                {section.label}
              </span>
              <div className="nav-section-items">
                {section.items.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    className={({ isActive }) => `nav-item ${isActive ? "is-active" : ""}`}
                    to={to}
                    key={to}
                  >
                    <Icon size={19} aria-hidden="true" />
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            </section>
          ))}
        </nav>
        <button
          className="sidebar-user"
          type="button"
          title={authenticationEvidence === "SIMULATED" ? "显式演示模式" : "退出当前员工账号"}
          onClick={() => logout.mutate()}
          disabled={authenticationEvidence === "SIMULATED" || logout.isPending}
        >
          <span className="user-avatar">{operator.displayName.slice(0, 1)}</span>
          <span>
            <strong>{operator.displayName}</strong>
            <small>
              {operator.role} · {operatorSessionEvidenceLabel(authenticationEvidence)}
            </small>
          </span>
          <LogOut size={17} aria-hidden="true" />
        </button>
        {logout.error instanceof Error ? (
          <div className="error-band sidebar-logout-error" role="alert">
            退出未完成：{logout.error.message}
          </div>
        ) : null}
      </aside>
      <main className="workspace">
        {sharedShiftSession ? (
          <output className="shared-shift-session-banner" aria-live="polite">
            <ShieldCheck size={18} aria-hidden="true" />
            <div>
              <strong>当前是公共收银设备</strong>
              <span>
                {sessionContext.shiftStatus === "OPEN"
                  ? "只在当前班次内有效；提交交班后会自动退出。"
                  : sessionContext.shiftStatus === "HANDOVER_PENDING"
                    ? "当前只允许核对并确认接班；确认后会自动退出。"
                    : "请先进入交接班开始班次；开始前不能办理经营业务。"}
              </span>
            </div>
          </output>
        ) : null}
        <ConnectivityBar />
        <Routes>
          <Route
            path="/frontdesk"
            element={
              <AuthorizedRoute operator={operator} path="/frontdesk">
                <FrontDesk operator={operator} />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/shifts"
            element={
              <AuthorizedRoute operator={operator} path="/shifts">
                <DeferredRoute>
                  <CashShiftDesk operator={operator} />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/coach"
            element={
              <AuthorizedRoute operator={operator} path="/coach">
                <DeferredRoute>
                  <CoachDesk operator={operator} />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <AuthorizedRoute operator={operator} path="/tasks">
                <TaskDesk operator={operator} />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <AuthorizedRoute operator={operator} path="/orders">
                <OrdersDesk />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/review"
            element={
              <AuthorizedRoute operator={operator} path="/review">
                <ReviewDesk />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <AuthorizedRoute operator={operator} path="/products">
                <DeferredRoute>
                  <ProductsDesk operator={operator} />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/members"
            element={
              <AuthorizedRoute operator={operator} path="/members">
                <DeferredRoute>
                  <MembersDesk operator={operator} />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/group-buy"
            element={
              <AuthorizedRoute operator={operator} path="/group-buy">
                <DeferredRoute>
                  <GroupBuyDesk
                    operator={operator}
                    authenticationEvidence={authenticationEvidence}
                  />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/staff"
            element={
              <AuthorizedRoute operator={operator} path="/staff">
                <StaffDesk operator={operator} />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/owner"
            element={
              <AuthorizedRoute operator={operator} path="/owner">
                <OwnerDesk operator={operator} />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/configuration"
            element={
              <AuthorizedRoute operator={operator} path="/configuration">
                <DeferredRoute>
                  <OperatingConfigurationDesk operator={operator} />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/system"
            element={
              <AuthorizedRoute operator={operator} path="/system">
                <SystemDesk operator={operator} />
              </AuthorizedRoute>
            }
          />
          <Route path="/help" element={<HelpDesk operator={operator} />} />
          <Route path="*" element={<Navigate to={defaultRoute} replace />} />
        </Routes>
      </main>
    </div>
  );
}

function ConnectivityBar(): React.JSX.Element {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["connectivity"],
    queryFn: getConnectivity,
    refetchInterval: 1_000,
  });
  const simulation = useMutation({
    mutationFn: simulateNetwork,
    onSuccess: async (result) => {
      queryClient.setQueryData(["connectivity"], result);
      await queryClient.invalidateQueries({ queryKey: ["tables"] });
    },
  });
  const status = query.data;
  if (!status && query.isPending) {
    return (
      <section className="connectivity-bar is-connecting" aria-live="polite">
        <RefreshCw className="spin" size={17} />
        <strong>正在连接门店网关</strong>
      </section>
    );
  }
  if (!status || !hasFreshConnectivity(query)) {
    return (
      <section className="connectivity-bar is-unreachable" role="alert">
        <WifiOff size={18} />
        <strong>门店网关不可达</strong>
        <span>请检查收银设备与门店主机是否连接在同一个局域网</span>
      </section>
    );
  }
  if (status.mode === "ONLINE") {
    return (
      <section className="online-status" aria-label="连接状态">
        <div className="connectivity-bar is-online">
          <Wifi size={17} />
          <span>云端同步正常</span>
          <small>上次同步 {formatTime(status.lastSuccessfulSyncAt)}</small>
          {status.simulationAllowed ? (
            <button
              className="simulation-button"
              type="button"
              onClick={() => simulation.mutate(true)}
              disabled={simulation.isPending}
            >
              <WifiOff size={15} />
              模拟断网
            </button>
          ) : null}
          <span className="evidence-label">演示网络</span>
        </div>
        {simulation.error instanceof Error ? (
          <div className="error-band">网络模拟失败：{simulation.error.message}</div>
        ) : null}
        {status.lastRecoverySummary ? (
          <div className="recovery-summary">
            最近恢复：已同步 {status.lastRecoverySummary.synced} 笔 · 仍待核对{" "}
            {status.lastRecoverySummary.pendingReconciliation} 笔 · 冲突{" "}
            {status.lastRecoverySummary.conflicts} 笔 · 已恢复{" "}
            {status.lastRecoverySummary.restoredFeatures.join("、")}
          </div>
        ) : null}
      </section>
    );
  }
  if (status.mode === "RECOVERING") {
    const presentation = connectivityRecoveryPresentation({
      failedRecords: status.syncProgress.failed,
      syncStage: status.syncStage,
    });
    const percentage =
      status.syncProgress.total === 0
        ? 100
        : Math.round((status.syncProgress.processed / status.syncProgress.total) * 100);
    return (
      <section
        className={`connectivity-panel ${presentation.blocked ? "is-blocked" : "is-recovering"}`}
        aria-live={presentation.blocked ? "assertive" : "polite"}
        role={presentation.blocked ? "alert" : undefined}
      >
        <div className="connectivity-title">
          {presentation.blocked ? (
            <AlertTriangle size={20} />
          ) : (
            <RefreshCw className="spin" size={20} />
          )}
          <div>
            <strong>{presentation.title}</strong>
            <span>{presentation.detail}</span>
          </div>
          <span className="evidence-label">演示网络</span>
        </div>
        <progress
          className="sync-progress"
          max={100}
          value={percentage}
          aria-label="离线记录同步进度"
          aria-valuetext={`${status.syncProgress.processed}/${status.syncProgress.total}，剩余 ${status.syncProgress.remaining}，失败 ${status.syncProgress.failed}`}
        />
        <div className="recovery-metrics">
          <span>
            进度 {status.syncProgress.processed}/{status.syncProgress.total}
          </span>
          <span>剩余 {status.syncProgress.remaining}</span>
          <span>失败 {status.syncProgress.failed}</span>
          <span>待渠道核对 {status.pendingReconciliationCount}</span>
        </div>
      </section>
    );
  }
  const authorityExpired = status.mode === "AUTHORITY_EXPIRED";
  return (
    <section
      className={`connectivity-panel ${authorityExpired ? "is-blocked" : "is-offline"}`}
      role="alert"
    >
      <div className="connectivity-title">
        <WifiOff size={21} />
        <div>
          <strong>{authorityExpired ? "离线写入权威已失效" : "当前处于离线营业模式"}</strong>
          <span>
            断网时长 {formatDuration(status.offlineDurationSeconds)} · 最后成功同步{" "}
            {formatTime(status.lastSuccessfulSyncAt)}
          </span>
        </div>
        <div className="offline-counts">
          <strong>{status.pendingRecords}</strong>
          <span>待同步记录</span>
        </div>
        {status.simulationAllowed ? (
          <button
            className="simulation-button"
            type="button"
            onClick={() => simulation.mutate(false)}
            disabled={simulation.isPending}
          >
            <Wifi size={15} />
            模拟恢复网络
          </button>
        ) : null}
        <span className="evidence-label">演示网络</span>
      </div>
      {simulation.error instanceof Error ? (
        <div className="error-band">网络模拟失败：{simulation.error.message}</div>
      ) : null}
      {authorityExpired ? (
        <p className="authority-warning">
          为防止双主，开台、转台、加退单、收款和关台已暂停；仍可查看本地可靠保存的台局与订单。
        </p>
      ) : (
        <div className="availability-grid">
          <div>
            <strong>当前可用</strong>
            <ul>
              {status.availableFeatures.map((feature) => (
                <li key={feature.code}>
                  <Check size={14} /> {feature.label}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <strong>当前不可用</strong>
            <ul>
              {status.unavailableFeatures.map((feature) => (
                <li key={feature.code} title={feature.reason}>
                  <X size={14} /> {feature.label}：{feature.reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}

function PageHeader({
  eyebrow,
  title,
  action,
}: {
  eyebrow: string;
  title: string;
  action?: React.ReactNode;
}): React.JSX.Element {
  return (
    <header className="topbar">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
      </div>
      <div className="topbar-status">{action}</div>
    </header>
  );
}

export function claimPendingSku(pending: Set<string>, skuId: string): boolean {
  if (pending.has(skuId)) return false;
  pending.add(skuId);
  return true;
}

export function preferProductAddOrder(
  current: OrderDetail | undefined,
  candidate: OrderDetail,
): OrderDetail {
  if (!current) return candidate;
  const currentQuantity = current.items.reduce((total, item) => total + item.quantity, 0);
  const candidateQuantity = candidate.items.reduce((total, item) => total + item.quantity, 0);
  return candidateQuantity >= currentQuantity ? candidate : current;
}

export function productAddIntroducedItem(
  current: OrderDetail | undefined,
  candidate: OrderDetail,
  skuId: string,
): boolean {
  const currentItemIds = new Set(current?.items.map((item) => item.id) ?? []);
  return candidate.items.some((item) => item.skuId === skuId && !currentItemIds.has(item.id));
}

function FrontDesk({ operator }: { operator: OperatorIdentity }): React.JSX.Element {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [selected, setSelected] = useState<TableSnapshot | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const openedTaskTarget = useRef<string | null>(null);
  const tablesQuery = useQuery({
    queryKey: ["tables"],
    queryFn: listTables,
    refetchInterval: 1_000,
  });
  const connectivity = useQuery({
    queryKey: ["connectivity"],
    queryFn: getConnectivity,
    refetchInterval: 1_000,
  });
  const devices = useQuery({
    queryKey: ["device-status"],
    queryFn: getDeviceAdapterStatus,
    refetchInterval: 2_000,
  });
  const tables = tablesQuery.data ?? [];
  const activeCount = tables.filter((table) => table.state === "IN_USE").length;
  const cleaningCount = tables.filter((table) => table.state === "AWAITING_CLEANING").length;

  useEffect(() => {
    const tableNodeId = new URLSearchParams(location.search).get("tableNodeId");
    if (!tableNodeId || openedTaskTarget.current === tableNodeId || tables.length === 0) return;
    openedTaskTarget.current = tableNodeId;
    const table = tables.find((candidate) => candidate.tableNodeId === tableNodeId);
    if (!table) {
      setNotice("未找到待办对应的球台，请刷新后再试");
      return;
    }
    setSelected(table);
    setNotice(`已打开 ${table.tableNumber} 号台，可核对当前状态并继续处理`);
  }, [location.search, tables]);

  return (
    <>
      <PageHeader
        eyebrow="北岸桌球俱乐部 · A 区"
        title="前台营业台"
        action={
          <>
            <span className="demo-pill">演示环境</span>
            <button
              type="button"
              className="icon-button"
              title="刷新球台"
              onClick={() => void tablesQuery.refetch()}
            >
              <RefreshCw size={18} aria-hidden="true" />
            </button>
          </>
        }
      />
      <section className="summary-strip" aria-label="营业概览">
        <div>
          <span>球台总数</span>
          <strong>{String(tables.length).padStart(2, "0")}</strong>
        </div>
        <div>
          <span>使用中</span>
          <strong>{String(activeCount).padStart(2, "0")}</strong>
        </div>
        <div>
          <span>待清台</span>
          <strong>{String(cleaningCount).padStart(2, "0")}</strong>
        </div>
        <div className="warning-summary">
          <AlertTriangle size={17} />
          <span>当前使用演示计费规则，不会作为正式门店账单</span>
        </div>
      </section>
      {devices.data?.attention.length ? (
        <section className="device-attention-band" role="alert">
          <AlertTriangle size={19} />
          <div>
            <strong>设备操作需要人工处理</strong>
            {devices.data.attention.slice(0, 5).map((effect) => (
              <span key={effect.id}>
                {effect.effectType === "LIGHT_ON"
                  ? "请人工开灯"
                  : effect.effectType === "LIGHT_OFF"
                    ? "请人工关灯"
                    : "请检查打印机并重新打印"}
                ：{effect.errorMessage ?? "设备没有返回明确结果"}（
                {effect.evidence === "MOCK" ? "测试设备" : "门店设备"}）
              </span>
            ))}
          </div>
        </section>
      ) : null}
      <LongSessionAlertPanel tables={tables} operator={operator} onSelect={setSelected} />
      <section className="table-section">
        <div className="section-heading">
          <div>
            <h2>球台状态</h2>
            <p>{tables.length} 张已绑定 · 以门店主机保存的状态为准</p>
          </div>
          <div className="legend">
            <span>
              <i className="legend-dot idle" />
              空闲
            </span>
            <span>
              <i className="legend-dot active" />
              使用中
            </span>
            <span>
              <i className="legend-dot issue" />
              待清台
            </span>
          </div>
        </div>
        {notice ? (
          <div className="success-band">
            <Check size={18} />
            <span>{notice}</span>
            <button type="button" title="关闭提示" onClick={() => setNotice(null)}>
              <X size={16} />
            </button>
          </div>
        ) : null}
        {tablesQuery.isError ? (
          <div className="error-band" role="alert">
            <AlertTriangle size={19} />
            <span>无法读取门店主机保存的球台状态</span>
          </div>
        ) : null}
        {tablesQuery.isPending ? (
          <div className="loading-state">正在读取门店网关...</div>
        ) : tables.length === 0 ? (
          <div className="empty-state">当前门店没有已绑定球台</div>
        ) : (
          <div className="table-grid">
            {tables.map((table) => (
              <TableTile key={table.tableId} table={table} onSelect={setSelected} />
            ))}
          </div>
        )}
      </section>
      <SessionDialog
        key={selected?.sessionId ?? selected?.tableId ?? "closed"}
        table={selected}
        tables={tables}
        operator={operator}
        status={hasFreshConnectivity(connectivity) ? connectivity.data : undefined}
        onClose={() => setSelected(null)}
        onChanged={async (message, close) => {
          setNotice(message);
          if (close) setSelected(null);
          await queryClient.invalidateQueries({ queryKey: ["tables"] });
          await queryClient.invalidateQueries({ queryKey: ["orders"] });
          await queryClient.invalidateQueries({ queryKey: ["manual-review"] });
          await queryClient.invalidateQueries({ queryKey: ["connectivity"] });
          await queryClient.invalidateQueries({ queryKey: ["device-status"] });
        }}
      />
    </>
  );
}

export function LongSessionAlertPanel({
  tables,
  operator,
  onSelect,
}: {
  tables: TableSnapshot[];
  operator: OperatorIdentity;
  onSelect?: (table: TableSnapshot) => void;
}): React.JSX.Element | null {
  const riskyTables = tables
    .filter(
      (table) => table.sessionRiskLevel === "WARNING" || table.sessionRiskLevel === "CRITICAL",
    )
    .sort((left, right) => (right.sessionElapsedSeconds ?? 0) - (left.sessionElapsedSeconds ?? 0));
  if (riskyTables.length === 0) return null;
  const canResolve = operator.role === "OWNER" || operator.role === "MANAGER";
  return (
    <section className="long-session-alert-panel" role="alert" aria-live="polite">
      <div className="long-session-alert-heading">
        <AlertTriangle size={20} />
        <div>
          <strong>发现 {riskyTables.length} 张疑似漏关台 / 超长台局</strong>
          <span>系统会持续提醒，不会静默累计金额。先核对现场，再由老板或店长授权处理。</span>
        </div>
      </div>
      <div className="long-session-alert-list">
        {riskyTables.map((table) => (
          <article key={table.tableId} className={table.sessionRiskLevel?.toLowerCase()}>
            <div>
              <strong>
                {table.tableNumber} 号台 ·
                {table.sessionRiskLevel === "CRITICAL" ? "严重超长" : "预警"}
              </strong>
              <span>
                开台{" "}
                {table.sessionStartedAt
                  ? new Date(table.sessionStartedAt).toLocaleString("zh-CN")
                  : "未知"}
                · 已持续 {formatElapsedSeconds(table.sessionElapsedSeconds ?? 0)}
              </span>
              <span>
                当前总计 {formatFee(table.currentTotalFen ?? table.estimatedFeeFen)} · 台费
                {formatFee(table.estimatedFeeFen)}
              </span>
            </div>
            {onSelect ? (
              <button className="button secondary" type="button" onClick={() => onSelect(table)}>
                {canResolve ? "核对并处理" : "查看台局"}
              </button>
            ) : (
              <NavLink
                className="button secondary"
                to={`/frontdesk?tableNodeId=${table.tableNodeId}`}
              >
                {canResolve ? "去前台处理" : "查看详情"}
              </NavLink>
            )}
          </article>
        ))}
      </div>
      {!canResolve ? (
        <p className="authorization-note">
          当前角色只能查看告警；资金与台局异常处置需通知老板或店长。
        </p>
      ) : null}
    </section>
  );
}

function formatElapsedSeconds(seconds: number): string {
  const totalMinutes = Math.max(0, Math.floor(seconds / 60));
  const days = Math.floor(totalMinutes / 1_440);
  const hours = Math.floor((totalMinutes % 1_440) / 60);
  const minutes = totalMinutes % 60;
  return days > 0 ? `${days} 天 ${hours} 小时 ${minutes} 分` : `${hours} 小时 ${minutes} 分`;
}

export function TableTile({
  table,
  onSelect,
}: {
  table: TableSnapshot;
  onSelect: (table: TableSnapshot) => void;
}): React.JSX.Element {
  const duration = useDuration(table.sessionStartedAt);
  const stateLabel =
    table.state === "IN_USE"
      ? "使用中"
      : table.state === "IDLE"
        ? "空闲"
        : table.state === "AWAITING_CLEANING"
          ? "待清台"
          : "不可用";
  return (
    <button
      className={`table-tile state-${table.state.toLowerCase()} risk-${(table.sessionRiskLevel ?? "NORMAL").toLowerCase()}`}
      type="button"
      onClick={() => onSelect(table)}
    >
      <span className="tile-rail" />
      <span className="tile-head">
        <span>
          <strong>{table.tableNumber}</strong>
          <small>{table.area}</small>
        </span>
        <span className="state-label">{stateLabel}</span>
      </span>
      <span className="tile-body">
        <span className="metric">
          <Clock3 size={15} />
          <span>{duration}</span>
        </span>
        {table.sessionRiskLevel === "WARNING" || table.sessionRiskLevel === "CRITICAL" ? (
          <span className={`long-session-badge ${table.sessionRiskLevel.toLowerCase()}`}>
            {table.sessionRiskLevel === "CRITICAL" ? "严重超长 · 疑似漏关台" : "超长台局预警"}
          </span>
        ) : null}
        <span className="table-fee-breakdown">
          <strong className="fee">
            {formatFee(table.currentTotalFen ?? table.estimatedFeeFen)}
          </strong>
          <small>当前总计 · 其中台费 {formatFee(table.estimatedFeeFen)}</small>
        </span>
        <small>
          {table.pricingPlanName ??
            (table.state === "AWAITING_CLEANING" ? "账单已结，等待清台" : "待选择计费方案")}
        </small>
      </span>
      <span className="tile-foot">
        <span className="evidence-tag">
          {table.billingEvidence === "SIMULATED" ? "模拟计费" : "门店规则"}
        </span>
        <ChevronRight size={17} />
      </span>
    </button>
  );
}

export type ClearTablePhase = {
  status: "IDLE" | "SUBMITTING" | "QUERYING" | "UNKNOWN" | "ERROR";
  requestId?: string | undefined;
  message?: string;
};

export function claimExclusiveAction(lock: { current: boolean }): boolean {
  if (lock.current) return false;
  lock.current = true;
  return true;
}

export function replaceTableSnapshot(
  current: TableSnapshot[] | undefined,
  snapshot: TableSnapshot,
): TableSnapshot[] {
  const existing = current ?? [];
  let replaced = false;
  const next = existing.map((candidate) => {
    if (candidate.tableId !== snapshot.tableId) return candidate;
    replaced = true;
    return snapshot;
  });
  return replaced ? next : [...next, snapshot];
}

export function ClearTableAction({
  phase,
  writeDisabled,
  onSubmit,
  onQuery,
}: {
  phase: ClearTablePhase;
  writeDisabled: boolean;
  onSubmit: () => void;
  onQuery: () => void;
}): React.JSX.Element {
  const busy = phase.status === "SUBMITTING" || phase.status === "QUERYING";
  const resultUnknown = phase.status === "UNKNOWN";
  const failed = phase.status === "ERROR";

  return (
    <div className="simple-action clear-table-action">
      <p>收款状态与订单已保存；清台不会修改已结账金额。</p>
      {phase.status === "SUBMITTING" ? (
        <output className="inline-operation-feedback is-info" aria-live="polite">
          <RefreshCw className="is-spinning" size={16} />
          <span>正在清台：正在提交门店状态，请勿重复操作。</span>
        </output>
      ) : null}
      {phase.status === "QUERYING" ? (
        <output className="inline-operation-feedback is-info" aria-live="polite">
          <RefreshCw className="is-spinning" size={16} />
          <span>正在查询清台结果，请稍候。</span>
        </output>
      ) : null}
      {resultUnknown ? (
        <output className="inline-operation-feedback is-info" aria-live="polite">
          <AlertTriangle size={16} />
          <span>
            <strong>清台结果待确认</strong>
            <br />
            {phase.message}
            {phase.requestId ? (
              <small className="clear-table-request">请求号：{phase.requestId}</small>
            ) : null}
          </span>
        </output>
      ) : null}
      {failed ? (
        <div className="inline-operation-feedback is-error" role="alert">
          <AlertTriangle size={16} />
          <span>
            <strong>清台未完成</strong>
            <br />
            {phase.message}
            {phase.requestId ? (
              <small className="clear-table-request">请求号：{phase.requestId}</small>
            ) : null}
          </span>
        </div>
      ) : null}
      <div className="clear-table-actions">
        {resultUnknown ? (
          <button className="button secondary" type="button" disabled={busy} onClick={onQuery}>
            <RefreshCw size={16} />
            查询清台结果
          </button>
        ) : null}
        <button
          className="button primary"
          type="button"
          disabled={busy || writeDisabled}
          onClick={onSubmit}
        >
          <Check size={16} />
          {phase.status === "SUBMITTING"
            ? "正在清台…"
            : resultUnknown
              ? "使用原请求号安全重试"
              : failed
                ? "重新尝试清台"
                : "确认清台"}
        </button>
      </div>
    </div>
  );
}

type SessionBusinessFlow = "OPEN" | "ORDER" | "BILLING" | "TRANSFER" | "CHECKOUT";

export function isSessionOperationErrorVisible(
  errorScope: SessionBusinessFlow | null,
  activeFlow: SessionBusinessFlow,
): boolean {
  return errorScope !== null && errorScope === activeFlow;
}

export function shouldResetSessionOperationErrorOnFlowChange(
  errorScope: SessionBusinessFlow | null,
  nextFlow: SessionBusinessFlow,
  hasSettledError: boolean,
): boolean {
  return hasSettledError && errorScope !== null && errorScope !== nextFlow;
}

export function sessionOperationWritePolicy({
  gatewayWriteDisabled,
  orderReadFailed,
  conflictIsolated,
}: {
  gatewayWriteDisabled: boolean;
  orderReadFailed: boolean;
  conflictIsolated: boolean;
}): {
  standardOrderWriteDisabled: boolean;
  longSessionResolutionDisabled: boolean;
} {
  return {
    standardOrderWriteDisabled: gatewayWriteDisabled || orderReadFailed || conflictIsolated,
    // This action is the audited escape hatch for a conflicted, abnormally long
    // session. It preserves and isolates the order instead of mutating its facts.
    longSessionResolutionDisabled: gatewayWriteDisabled || orderReadFailed,
  };
}

function SessionDialog({
  table,
  tables,
  operator,
  status,
  onClose,
  onChanged,
}: {
  table: TableSnapshot | null;
  tables: TableSnapshot[];
  operator: OperatorIdentity;
  status: GatewayStatus | undefined;
  onClose: () => void;
  onChanged: (message: string, close: boolean) => Promise<void>;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const duration = useDuration(table?.sessionStartedAt ?? null);
  const [tab, setTab] = useState<"ORDER" | "BILLING" | "TRANSFER" | "CHECKOUT">("ORDER");
  const [operationErrorScope, setOperationErrorScope] = useState<SessionBusinessFlow | null>(null);
  const [returnCompletionSequence, setReturnCompletionSequence] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("CASH");
  const [targetTableId, setTargetTableId] = useState("");
  const [transferReason, setTransferReason] = useState("");
  const pendingSkuIdsRef = useRef<Set<string>>(new Set());
  const [pendingSkuIds, setPendingSkuIds] = useState<ReadonlySet<string>>(() => new Set());
  const [productFeedback, setProductFeedback] = useState<string | null>(null);
  const [productFeedbackKind, setProductFeedbackKind] = useState<"INFO" | "SUCCESS" | "ERROR">(
    "INFO",
  );
  const clearInFlightRef = useRef(false);
  const clearRequestIdRef = useRef<string | null>(null);
  const [clearPhase, setClearPhase] = useState<ClearTablePhase>({ status: "IDLE" });
  const productOrderSelected =
    status?.selectedConditionalModules?.includes("PRODUCT_ORDER") ?? false;
  const targetTableSelectId = useId();
  const dialogTitleId = useId();
  const dialogDescriptionId = useId();
  useEffect(() => {
    if (status && !productOrderSelected && tab === "ORDER") setTab("TRANSFER");
  }, [productOrderSelected, status, tab]);
  const orderQuery = useQuery({
    queryKey: ["session-order", table?.sessionId],
    queryFn: () => getOrder(table?.sessionId ?? ""),
    enabled: Boolean(table?.sessionId),
    refetchInterval: table?.state === "IN_USE" ? 1_000 : false,
  });
  const productsQuery = useQuery({
    queryKey: ["products"],
    queryFn: listProducts,
    enabled: table?.state === "IN_USE" && productOrderSelected,
  });
  const addProduct = useMutation({
    mutationFn: async (product: ProductSnapshot) => {
      if (!table?.sessionId) throw new Error("进行中台单缺少 sessionId");
      const sessionId = table.sessionId;
      const result = await runIdempotentOperation(
        `add:${sessionId}:${product.skuId}:1`,
        async (idempotencyKey) => addOrderItem(sessionId, product.skuId, 1, idempotencyKey),
      );
      return { product, result, sessionId };
    },
    onSuccess: ({ product, result, sessionId }) => {
      let introducedNewItem = false;
      queryClient.setQueryData<OrderDetail>(["session-order", sessionId], (current) => {
        introducedNewItem = productAddIntroducedItem(current, result, product.skuId);
        return preferProductAddOrder(current, result);
      });
      if (introducedNewItem) {
        queryClient.setQueryData<ProductSnapshot[]>(["products"], (current) =>
          current?.map((candidate) => {
            if (
              candidate.skuId !== product.skuId ||
              candidate.trackInventory !== true ||
              candidate.stockOnHand === undefined
            ) {
              return candidate;
            }
            const nextStock = candidate.stockOnHand - 1;
            return {
              ...candidate,
              stockOnHand: candidate.allowNegativeStock ? nextStock : Math.max(0, nextStock),
            };
          }),
        );
      }
      setProductFeedback(`已把“${product.name}”加入订单，库存已扣减；可以继续点其他商品。`);
      setProductFeedbackKind("SUCCESS");
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ["session-order", sessionId] }),
        queryClient.invalidateQueries({ queryKey: ["products"] }),
        queryClient.invalidateQueries({ queryKey: ["tables"] }),
      ]);
    },
    onError: (error, product) => {
      const reason = error instanceof Error ? error.message : "系统没有返回明确原因";
      setProductFeedback(
        `“${product.name}”未加入订单：${reason}。库存没有扣减，确认原因后可安全重试。`,
      );
      setProductFeedbackKind("ERROR");
    },
    onSettled: (_result, _error, product) => {
      pendingSkuIdsRef.current.delete(product.skuId);
      setPendingSkuIds(new Set(pendingSkuIdsRef.current));
    },
  });
  const clearOperation = useMutation({
    mutationFn: async () => {
      if (!table) throw new Error("未选择球台");
      const tableId = table.tableId;
      return runIdempotentOperation(`clear:${tableId}`, async (idempotencyKey) => {
        clearRequestIdRef.current = idempotencyKey;
        setClearPhase({ status: "SUBMITTING", requestId: idempotencyKey });
        return {
          requestId: idempotencyKey,
          snapshot: await clearTable(tableId, idempotencyKey),
        };
      });
    },
    onSuccess: async ({ snapshot }) => {
      queryClient.setQueryData<TableSnapshot[]>(["tables"], (current) =>
        replaceTableSnapshot(current, snapshot),
      );
      await onChanged("清台完成，球台已恢复空闲。", true);
    },
    onError: (error) => {
      const requestId = clearRequestIdRef.current ?? undefined;
      if (error instanceof ApiRequestError && error.code === "GATEWAY_UNREACHABLE") {
        setClearPhase({
          status: "UNKNOWN",
          requestId,
          message:
            "网络超时或门店网关暂时不可达，系统不会盲目重复清台。请先查询结果；如仍为待清台，可使用原请求号安全重试。",
        });
        return;
      }
      const reason = error instanceof Error ? error.message : "系统没有返回明确原因";
      setClearPhase({
        status: "ERROR",
        requestId,
        message: `${reason}。账单与收款结果没有改变，确认原因后可安全重试。`,
      });
    },
    onSettled: () => {
      clearInFlightRef.current = false;
    },
  });
  const queryClearResult = async (): Promise<void> => {
    const requestId = clearRequestIdRef.current ?? undefined;
    setClearPhase({ status: "QUERYING", requestId });
    try {
      if (!table) throw new Error("未选择球台");
      const snapshot = (await listTables()).find(
        (candidate) => candidate.tableId === table.tableId,
      );
      if (!snapshot) throw new Error("门店网关没有返回该球台状态");
      queryClient.setQueryData<TableSnapshot[]>(["tables"], (current) =>
        replaceTableSnapshot(current, snapshot),
      );
      if (snapshot.state === "IDLE") {
        await onChanged("已按请求号确认清台完成，球台已恢复空闲。", true);
        return;
      }
      setClearPhase({
        status: "UNKNOWN",
        requestId,
        message: "门店网关仍显示待清台；账单与收款结果没有改变，可使用原请求号安全重试。",
      });
    } catch (error) {
      const reason = error instanceof Error ? error.message : "系统没有返回明确原因";
      setClearPhase({
        status: "UNKNOWN",
        requestId,
        message: `暂时无法确认清台结果：${reason}。请保持本页并稍后再次查询。`,
      });
    }
  };
  const operation = useMutation({
    mutationFn: async (action: {
      type: string;
      itemId?: string;
      reason?: string;
      managerLogin?: string;
      managerPin?: string;
      startedAt?: string;
      member?: { memberId: string; memberBalanceFen?: number };
    }) => {
      if (!table) throw new Error("未选择球台");
      if (action.type === "OPEN")
        return runIdempotentOperation(`open:${table.tableId}`, async (idempotencyKey) => ({
          kind: "OPEN",
          result: await openSession(table.tableId, idempotencyKey),
        }));
      if (!table.sessionId) throw new Error("进行中台单缺少 sessionId");
      if (action.type === "RETURN" && action.itemId && action.reason && action.managerPin) {
        const authorization = await requestSecondaryAuthorization(
          "RETURN_ORDER_ITEM",
          action.managerPin,
          action.managerLogin,
        );
        return runIdempotentOperation(
          `return:${table.sessionId}:${action.itemId}:1:${action.reason}`,
          async (idempotencyKey) => ({
            kind: "RETURN",
            result: await returnOrderItem(
              table.sessionId ?? "",
              action.itemId ?? "",
              1,
              action.reason ?? "",
              idempotencyKey,
              authorization.token,
            ),
          }),
        );
      }
      if (action.type === "TRANSFER" && action.reason)
        return runIdempotentOperation(
          `transfer:${table.sessionId}:${targetTableId}:${action.reason}`,
          async (idempotencyKey) => ({
            kind: "TRANSFER",
            result: await transferSession(
              table.sessionId ?? "",
              targetTableId,
              idempotencyKey,
              action.reason,
            ),
          }),
        );
      if (action.type === "PAUSE" && action.reason)
        return runIdempotentOperation(
          `pause:${table.sessionId}:${action.reason}`,
          async (idempotencyKey) => ({
            kind: "PAUSE",
            result: await pauseSession(table.sessionId ?? "", action.reason ?? "", idempotencyKey),
          }),
        );
      if (action.type === "RESUME" && action.reason)
        return runIdempotentOperation(
          `resume:${table.sessionId}:${action.reason}`,
          async (idempotencyKey) => ({
            kind: "RESUME",
            result: await resumeSession(table.sessionId ?? "", action.reason ?? "", idempotencyKey),
          }),
        );
      if (action.type === "END_EARLY" && action.reason)
        return runIdempotentOperation(
          `end-early:${table.sessionId}:${action.reason}`,
          async (idempotencyKey) => ({
            kind: "END_EARLY",
            result: await endSessionEarly(
              table.sessionId ?? "",
              action.reason ?? "",
              idempotencyKey,
            ),
          }),
        );
      if (action.type === "RESOLVE_LONG_SESSION" && action.reason && action.managerPin) {
        const authorization = await requestSecondaryAuthorization(
          "ADJUST_TABLE_SESSION",
          action.managerPin,
          action.managerLogin,
        );
        return runIdempotentOperation(
          `resolve-long-session:${table.sessionId}:${action.reason}`,
          async (idempotencyKey) => ({
            kind: "RESOLVE_LONG_SESSION",
            result: await resolveLongSession(
              table.sessionId ?? "",
              action.reason ?? "",
              idempotencyKey,
              authorization.token,
            ),
          }),
        );
      }
      if (
        action.type === "ADJUST_START" &&
        action.reason &&
        action.managerPin &&
        action.startedAt
      ) {
        const authorization = await requestSecondaryAuthorization(
          "ADJUST_TABLE_SESSION",
          action.managerPin,
          action.managerLogin,
        );
        return runIdempotentOperation(
          `adjust-start:${table.sessionId}:${action.startedAt}:${action.reason}`,
          async (idempotencyKey) => ({
            kind: "ADJUST_START",
            result: await adjustSessionStart(
              table.sessionId ?? "",
              action.startedAt ?? "",
              action.reason ?? "",
              idempotencyKey,
              authorization.token,
            ),
          }),
        );
      }
      if (action.type === "VOID" && action.reason && action.managerPin) {
        const authorization = await requestSecondaryAuthorization(
          "ADJUST_TABLE_SESSION",
          action.managerPin,
          action.managerLogin,
        );
        return runIdempotentOperation(
          `void:${table.sessionId}:${action.reason}`,
          async (idempotencyKey) => ({
            kind: "VOID",
            result: await voidSession(
              table.sessionId ?? "",
              action.reason ?? "",
              idempotencyKey,
              authorization.token,
            ),
          }),
        );
      }
      if (action.type === "CHECKOUT")
        return runIdempotentOperation(
          `checkout:${table.sessionId}:${paymentMethod}:${action.member?.memberId ?? "walk-in"}:${action.member?.memberBalanceFen ?? "full"}`,
          async (idempotencyKey) => {
            const result = await checkoutSession(
              table.sessionId ?? "",
              paymentMethod,
              idempotencyKey,
              action.member,
            );
            return { kind: "CHECKOUT", result, zeroAmountCheckout: result.order.totalFen === 0 };
          },
        );
      throw new Error("未知操作");
    },
    onSuccess: async (outcome) => {
      const { kind } = outcome;
      setOperationErrorScope(null);
      const zeroAmountCheckout =
        "zeroAmountCheckout" in outcome && outcome.zeroAmountCheckout === true;
      const close =
        kind === "OPEN" ||
        kind === "TRANSFER" ||
        kind === "CHECKOUT" ||
        kind === "VOID" ||
        kind === "RESOLVE_LONG_SESSION";
      const message =
        kind === "CHECKOUT"
          ? zeroAmountCheckout
            ? "团购券已覆盖本单应收，无需收现金或扫码；小票已交给打印适配器，球台进入待清台。"
            : paymentMethod === "MEMBER_BALANCE"
              ? "会员余额已由中心账本准确扣减一次，小票已交给打印设备；球台进入待清台。"
              : paymentMethod === "CASH"
                ? "现金已本地确认，小票已交给打印适配器；球台进入待清台。"
                : "已结账并关台；静态码付款保留“待渠道核对”，小票已交给打印适配器。"
          : kind === "RETURN"
            ? "商品退单已完成经理二次授权并写入审计。"
            : kind === "TRANSFER"
              ? "台单已转移到目标球台；系统已按本单转台规则延续或重新锁定价格，请核对计费明细。"
              : kind === "PAUSE"
                ? "计费已暂停；是否收费按本单已锁定的价格规则执行。"
                : kind === "RESUME"
                  ? "计费已恢复，暂停区间和操作原因已写入审计。"
                  : kind === "ADJUST_START"
                    ? "开台时间已由经理授权修改，原值、新值和原因已留痕。"
                    : kind === "END_EARLY"
                      ? "计费已提前停止，金额已冻结；请继续核对商品并结账。"
                      : kind === "RESOLVE_LONG_SESSION"
                        ? "超长台局已停止继续计费，原订单和金额已隔离到待核对，球台已恢复空闲。"
                        : kind === "VOID"
                          ? "误开台已由经理授权作废，球台已恢复空闲。"
                          : "开台成功，本单采用的价格已经保存。";
      await onChanged(message, close);
      if (!close) await orderQuery.refetch();
      if (kind === "RETURN") {
        setReturnCompletionSequence((current) => current + 1);
        await queryClient.invalidateQueries({ queryKey: ["products"] });
        await productsQuery.refetch();
      }
    },
  });
  const submitSessionOperation = (
    scope: SessionBusinessFlow,
    action: Parameters<typeof operation.mutate>[0],
  ): void => {
    setOperationErrorScope(scope);
    operation.mutate(action);
  };
  const clearSettledOperationError = (): void => {
    if (!operation.isError) return;
    operation.reset();
    setOperationErrorScope(null);
  };
  const selectSessionTab = (nextTab: "ORDER" | "BILLING" | "TRANSFER" | "CHECKOUT"): void => {
    if (
      shouldResetSessionOperationErrorOnFlowChange(operationErrorScope, nextTab, operation.isError)
    ) {
      operation.reset();
      setOperationErrorScope(null);
    }
    setTab(nextTab);
  };
  const closeSessionDialog = (): void => {
    operation.reset();
    setOperationErrorScope(null);
    setTab(productOrderSelected ? "ORDER" : "TRANSFER");
    onClose();
  };
  const writeDisabled = !status?.offlineAuthorityAvailable;
  const order = orderQuery.data;
  const conflictIsolated = order?.status === "CONFLICT" || order?.syncState === "CONFLICT";
  const writePolicy = sessionOperationWritePolicy({
    gatewayWriteDisabled: writeDisabled,
    orderReadFailed: orderQuery.isError,
    conflictIsolated,
  });
  const orderWriteDisabled = writePolicy.standardOrderWriteDisabled;
  const idleTargets = tables.filter(
    (candidate) => candidate.state === "IDLE" && candidate.tableId !== table?.tableId,
  );

  return (
    <CspModal
      open={table !== null}
      onOpenChange={(open) => !open && closeSessionDialog()}
      labelledBy={dialogTitleId}
      describedBy={dialogDescriptionId}
      className="session-dialog"
    >
      <h2 id={dialogTitleId}>{table?.tableNumber} 号台</h2>
      <p id={dialogDescriptionId}>
        {table?.state === "IDLE"
          ? "空闲球台"
          : table?.state === "AWAITING_CLEANING"
            ? "账单已结，等待员工清台"
            : `${duration} · ${formatFee(order?.totalFen ?? table?.estimatedFeeFen ?? 0)}`}
      </p>
      {operation.error instanceof Error &&
      isSessionOperationErrorVisible(
        operationErrorScope,
        table?.state === "IDLE" ? "OPEN" : tab,
      ) ? (
        <div className="dialog-error">{operation.error.message}</div>
      ) : null}
      {writeDisabled ? (
        <div className="disabled-reason">
          <AlertTriangle size={16} />
          门店主机的离线操作权限已过期。为防止两台设备同时改账，当前只能查看，不能新增或修改。
        </div>
      ) : null}
      {table?.state === "IDLE" ? (
        <div className="simple-action">
          <div className="dialog-facts">
            <span>
              <ShieldCheck size={16} />
              操作人：前台 01
            </span>
            <span>
              <CircleDollarSign size={16} />
              当前价格：演示规则，不会作为正式门店账单
            </span>
            <span>
              <Power size={16} />
              开台后会向测试灯控发送开灯指令；请现场确认灯是否点亮
            </span>
          </div>
          <button
            className="button primary"
            type="button"
            disabled={operation.isPending || writeDisabled}
            onClick={() => submitSessionOperation("OPEN", { type: "OPEN" })}
          >
            确认开台
          </button>
        </div>
      ) : table?.state === "AWAITING_CLEANING" ? (
        <ClearTableAction
          phase={clearPhase}
          writeDisabled={writeDisabled}
          onSubmit={() => {
            if (!claimExclusiveAction(clearInFlightRef)) return;
            setClearPhase({
              status: "SUBMITTING",
              requestId: clearRequestIdRef.current ?? undefined,
            });
            clearOperation.mutate();
          }}
          onQuery={() => void queryClearResult()}
        />
      ) : table?.state === "IN_USE" ? (
        <>
          <div className="dialog-tabs" role="tablist">
            {productOrderSelected ? (
              <button
                type="button"
                className={tab === "ORDER" ? "is-active" : ""}
                onClick={() => selectSessionTab("ORDER")}
              >
                <PackagePlus size={16} />
                商品
              </button>
            ) : null}
            <button
              type="button"
              className={tab === "BILLING" ? "is-active" : ""}
              onClick={() => selectSessionTab("BILLING")}
            >
              <Clock3 size={16} />
              计费操作
            </button>
            <button
              type="button"
              className={tab === "TRANSFER" ? "is-active" : ""}
              onClick={() => selectSessionTab("TRANSFER")}
            >
              <ArrowRightLeft size={16} />
              转台
            </button>
            <button
              type="button"
              className={tab === "CHECKOUT" ? "is-active" : ""}
              onClick={() => selectSessionTab("CHECKOUT")}
            >
              <Banknote size={16} />
              结账
            </button>
          </div>
          <div className="order-totals">
            <span>
              台费 <strong>{formatFee(order?.tableFeeFen ?? table.estimatedFeeFen)}</strong>
            </span>
            <span>
              商品 <strong>{formatFee(order?.productTotalFen ?? 0)}</strong>
            </span>
            <span>
              助教服务 <strong>{formatFee(order?.coachServiceTotalFen ?? 0)}</strong>
            </span>
            <span>
              合计 <strong>{formatFee(order?.totalFen ?? table.estimatedFeeFen)}</strong>
            </span>
          </div>
          {!productOrderSelected ? (
            <div className="disabled-reason">
              <AlertTriangle size={16} />
              商品正式下单模块未选择；商品目录、加单和退单已禁用
            </div>
          ) : null}
          {orderQuery.isPending ? (
            <div className="loading-state">正在读取台单与金额...</div>
          ) : orderQuery.isError ? (
            <div className="error-band">
              台单读取失败；转台仍可继续，商品和收款已暂停以避免错误金额。
            </div>
          ) : null}
          {conflictIsolated ? (
            <div className="disabled-reason">
              <AlertTriangle size={16} />
              订单存在同步冲突，商品、转台和结账已隔离；请到“待核对”工作区转交账务复核
            </div>
          ) : null}
          {tab === "ORDER" && productOrderSelected ? (
            <OrderEditor
              key={`order-editor-${returnCompletionSequence}`}
              order={order}
              products={productsQuery.data ?? []}
              productsLoading={productsQuery.isPending}
              productsError={productsQuery.isError}
              pendingSkuIds={pendingSkuIds}
              returnPending={operation.isPending}
              productFeedback={productFeedback}
              productFeedbackKind={productFeedbackKind}
              disabled={orderWriteDisabled || !productOrderSelected}
              onAdd={(product) => {
                if (operation.isPending) return;
                if (!claimPendingSku(pendingSkuIdsRef.current, product.skuId)) return;
                setPendingSkuIds(new Set(pendingSkuIdsRef.current));
                setProductFeedback(`正在把“${product.name}”加入订单…`);
                setProductFeedbackKind("INFO");
                addProduct.mutate(product);
              }}
              onReturn={(itemId, reason, managerLogin, managerPin) =>
                submitSessionOperation("ORDER", {
                  type: "RETURN",
                  itemId,
                  reason,
                  managerLogin,
                  managerPin,
                })
              }
              onReturnFormReset={clearSettledOperationError}
            />
          ) : null}
          {tab === "BILLING" ? (
            <BillingOperationPanel
              order={order}
              table={table}
              operator={operator}
              pending={operation.isPending}
              disabled={orderWriteDisabled}
              longSessionResolutionDisabled={writePolicy.longSessionResolutionDisabled}
              onAction={(action) => submitSessionOperation("BILLING", action)}
            />
          ) : null}
          {tab === "TRANSFER" ? (
            <div className="transfer-panel">
              <label htmlFor={targetTableSelectId}>目标球台</label>
              <select
                id={targetTableSelectId}
                value={targetTableId}
                onChange={(event) => setTargetTableId(event.target.value)}
              >
                <option value="">请选择空闲球台</option>
                {idleTargets.map((candidate) => (
                  <option key={candidate.tableId} value={candidate.tableId}>
                    {candidate.tableNumber} 号台 · {candidate.area}
                  </option>
                ))}
              </select>
              <label>
                转台原因
                <input
                  value={transferReason}
                  onChange={(event) => setTransferReason(event.target.value)}
                  placeholder="例如：顾客要求换到 8 号台"
                  maxLength={200}
                />
              </label>
              <button
                className="button primary"
                type="button"
                disabled={
                  !targetTableId ||
                  transferReason.trim().length < 2 ||
                  operation.isPending ||
                  orderWriteDisabled
                }
                onClick={() =>
                  submitSessionOperation("TRANSFER", {
                    type: "TRANSFER",
                    reason: transferReason.trim(),
                  })
                }
              >
                <ArrowRightLeft size={16} />
                确认转台
              </button>
            </div>
          ) : null}
          {tab === "CHECKOUT" ? (
            <CheckoutPanel
              method={paymentMethod}
              onMethod={setPaymentMethod}
              order={order}
              pending={operation.isPending}
              disabled={orderWriteDisabled}
              memberFundsAvailable={status?.serviceHealth.memberFunds ?? false}
              manualPaymentMethods={status?.manualPaymentMethods}
              onCheckout={(member) =>
                submitSessionOperation("CHECKOUT", {
                  type: "CHECKOUT",
                  ...(member ? { member } : {}),
                })
              }
            />
          ) : null}
        </>
      ) : (
        <div className="disabled-reason">该球台当前不可操作</div>
      )}
      <button
        className="dialog-close icon-button"
        type="button"
        title="关闭"
        onClick={closeSessionDialog}
      >
        <X size={18} />
      </button>
    </CspModal>
  );
}

export type DisplayOrderItemGroup = {
  key: string;
  skuId: string;
  name: string;
  unitPriceFen: number;
  evidence: OrderItemRecord["evidence"];
  quantity: number;
  returnedQuantity: number;
  availableQuantity: number;
  batches: OrderItemRecord[];
};

const orderItemSalesConditionFields = [
  "priceSnapshotId",
  "priceSnapshotVersion",
  "specification",
  "variantId",
  "discountId",
  "discountFen",
  "discountRateBasisPoints",
  "salesConditionId",
  "salesConditionVersion",
] as const;

function orderItemDisplayGroupKey(item: OrderItemRecord): string {
  const extensibleItem = item as unknown as Record<string, unknown>;
  return JSON.stringify([
    item.skuId,
    item.name,
    item.unitPriceFen,
    item.evidence,
    ...orderItemSalesConditionFields.map((field) => [field, extensibleItem[field] ?? null]),
  ]);
}

export function groupOrderItemsForDisplay(
  items: readonly OrderItemRecord[],
): DisplayOrderItemGroup[] {
  const groups = new Map<string, DisplayOrderItemGroup>();
  for (const item of items) {
    const key = orderItemDisplayGroupKey(item);
    const existing = groups.get(key);
    if (existing) {
      existing.quantity += item.quantity;
      existing.returnedQuantity += item.returnedQuantity;
      existing.availableQuantity += item.quantity - item.returnedQuantity;
      existing.batches.push(item);
      continue;
    }
    groups.set(key, {
      key,
      skuId: item.skuId,
      name: item.name,
      unitPriceFen: item.unitPriceFen,
      evidence: item.evidence,
      quantity: item.quantity,
      returnedQuantity: item.returnedQuantity,
      availableQuantity: item.quantity - item.returnedQuantity,
      batches: [item],
    });
  }
  return [...groups.values()];
}

export function pickDefaultReturnBatch(
  group: DisplayOrderItemGroup | undefined,
): OrderItemRecord | null {
  if (!group) return null;
  for (let index = group.batches.length - 1; index >= 0; index -= 1) {
    const item = group.batches[index];
    if (item && item.quantity > item.returnedQuantity) return item;
  }
  return null;
}

export function OrderEditor({
  order,
  products,
  productsLoading,
  productsError,
  pending,
  pendingSkuIds,
  returnPending,
  productFeedback,
  productFeedbackKind,
  disabled,
  onAdd,
  onReturn,
  onReturnFormReset,
}: {
  order: OrderDetail | undefined;
  products: ProductSnapshot[];
  productsLoading: boolean;
  productsError: boolean;
  pending?: boolean;
  pendingSkuIds?: ReadonlySet<string>;
  returnPending?: boolean;
  productFeedback?: string | null;
  productFeedbackKind?: "INFO" | "SUCCESS" | "ERROR";
  disabled: boolean;
  onAdd: (product: ProductSnapshot) => void;
  onReturn: (itemId: string, reason: string, managerLogin: string, managerPin: string) => void;
  onReturnFormReset?: () => void;
}): React.JSX.Element {
  const [returnDraft, setReturnDraft] = useState<{
    itemId: string;
    targetLabel: string;
    reason: string;
    managerLogin: string;
    managerPin: string;
  } | null>(null);
  const [expandedItemGroups, setExpandedItemGroups] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const anyAddPending = (pendingSkuIds?.size ?? 0) > 0;
  const effectiveReturnPending = returnPending ?? pending ?? false;
  const itemGroups = groupOrderItemsForDisplay(order?.items ?? []);
  const openReturnDraft = (item: OrderItemRecord, targetLabel: string): void => {
    onReturnFormReset?.();
    setReturnDraft({
      itemId: item.id,
      targetLabel,
      reason: "",
      managerLogin: "",
      managerPin: "",
    });
  };
  return (
    <div className="order-editor">
      {productFeedback ? (
        <div
          className={`inline-operation-feedback is-${(productFeedbackKind ?? "INFO").toLowerCase()}`}
          role={productFeedbackKind === "ERROR" ? "alert" : "status"}
          aria-live="polite"
        >
          {productFeedbackKind === "ERROR" ? (
            <AlertTriangle size={16} aria-hidden="true" />
          ) : (
            <PackagePlus size={16} aria-hidden="true" />
          )}
          <span>{productFeedback}</span>
        </div>
      ) : null}
      <div className="product-picker">
        {productsLoading ? (
          <div className="compact-empty">正在读取商品...</div>
        ) : productsError ? (
          <div className="disabled-reason">商品目录读取失败；不影响转台和现金结账。</div>
        ) : products.length ? (
          products.map((product) => {
            const outOfStock =
              product.trackInventory === true &&
              (product.stockOnHand ?? 0) <= 0 &&
              product.allowNegativeStock !== true;
            const stockReason = outOfStock ? `缺货：库存 0${product.unit ?? "件"}，无法销售` : null;
            const skuPending = pendingSkuIds?.has(product.skuId) ?? pending ?? false;
            return (
              <button
                aria-label={
                  stockReason
                    ? `${product.name} ${formatFee(product.unitPriceFen)} ${stockReason}`
                    : `${product.name} ${formatFee(product.unitPriceFen)} ${product.evidence === "SIMULATED" ? "演示商品" : "门店商品"}`
                }
                type="button"
                key={product.skuId}
                disabled={skuPending || effectiveReturnPending || disabled || outOfStock}
                title={stockReason ?? undefined}
                onClick={() => onAdd(product)}
              >
                <PackagePlus size={16} />
                <span>{product.name}</span>
                <strong>{formatFee(product.unitPriceFen)}</strong>
                <small>
                  {skuPending
                    ? "正在加入订单…"
                    : (stockReason ?? (product.evidence === "SIMULATED" ? "演示商品" : "门店商品"))}
                </small>
              </button>
            );
          })
        ) : (
          <div className="compact-empty">当前没有可售商品</div>
        )}
      </div>
      <div className="line-items">
        <h3>已加商品</h3>
        {itemGroups.length ? (
          itemGroups.map((group) => {
            const expanded = expandedItemGroups.has(group.key);
            const defaultReturnBatch = pickDefaultReturnBatch(group);
            return (
              <div className="line-item-group" key={group.key}>
                <div className="line-item">
                  <span>
                    <strong>
                      {group.name} × {group.availableQuantity}
                    </strong>
                    <small>
                      原加 {group.quantity} 件 · 已退 {group.returnedQuantity} · 单价{" "}
                      {formatFee(group.unitPriceFen)}
                    </small>
                  </span>
                  <span>{formatFee(group.availableQuantity * group.unitPriceFen)}</span>
                  <button
                    className="icon-button"
                    type="button"
                    title="减少 1 件（默认从最近加单批次退，需要经理二次授权）"
                    aria-label={`减少 ${group.name} 1 件`}
                    disabled={
                      effectiveReturnPending || anyAddPending || disabled || !defaultReturnBatch
                    }
                    onClick={() => {
                      if (!defaultReturnBatch) return;
                      openReturnDraft(defaultReturnBatch, `${group.name} · 最近一笔可退批次`);
                    }}
                  >
                    <Minus size={15} />
                  </button>
                </div>
                {group.batches.length > 1 ? (
                  <button
                    className="order-item-batch-toggle"
                    type="button"
                    aria-expanded={expanded}
                    onClick={() =>
                      setExpandedItemGroups((current) => {
                        const next = new Set(current);
                        if (next.has(group.key)) next.delete(group.key);
                        else next.add(group.key);
                        return next;
                      })
                    }
                  >
                    {expanded ? "收起" : "查看"} {group.batches.length} 笔加单记录
                  </button>
                ) : null}
                {expanded ? (
                  <fieldset className="order-item-batches">
                    <legend className="visually-hidden">{group.name} 加单批次</legend>
                    {group.batches.map((item, index) => {
                      const available = item.quantity - item.returnedQuantity;
                      return (
                        <div className="order-item-batch" key={item.id}>
                          <span>
                            第 {index + 1} 笔 · 批次 {item.id.slice(0, 8)} · {item.quantity} 件 ·
                            已退 {item.returnedQuantity}
                          </span>
                          <button
                            className="button secondary compact"
                            type="button"
                            disabled={
                              effectiveReturnPending || anyAddPending || disabled || available === 0
                            }
                            onClick={() =>
                              openReturnDraft(item, `${group.name} · 第 ${index + 1} 笔加单`)
                            }
                          >
                            指定此批次退 1 件
                          </button>
                        </div>
                      );
                    })}
                  </fieldset>
                ) : null}
              </div>
            );
          })
        ) : (
          <p className="compact-empty">尚未添加商品</p>
        )}
        <small className="authorization-note">
          <ShieldCheck size={13} />
          退单需要经理或老板本人二次验证，并记录操作人与授权人
        </small>
        {returnDraft ? (
          <form
            className="secondary-authorization-panel"
            name="order-item-return-authorization"
            autoComplete="off"
            onSubmit={(event) => {
              event.preventDefault();
              onReturn(
                returnDraft.itemId,
                returnDraft.reason.trim(),
                returnDraft.managerLogin.trim(),
                returnDraft.managerPin,
              );
            }}
          >
            <strong>经理二次授权退单</strong>
            <small className="order-return-target">正在退：{returnDraft.targetLabel}</small>
            <AutofillSafeInput
              fieldName="order-item-return-reason"
              aria-label="退单原因"
              placeholder="填写退单原因"
              value={returnDraft.reason}
              onUserChange={(value) =>
                setReturnDraft((current) => (current ? { ...current, reason: value } : current))
              }
            />
            <AutofillSafeInput
              fieldName="order-item-return-authorizer-login"
              aria-label="授权人登录名"
              placeholder="店长或老板登录名"
              value={returnDraft.managerLogin}
              onUserChange={(value) =>
                setReturnDraft((current) =>
                  current ? { ...current, managerLogin: value } : current,
                )
              }
            />
            <AutofillSafeInput
              fieldName="order-item-return-authorization"
              aria-label="经理密码或演示授权码"
              placeholder="经理密码 / 演示授权码"
              type="password"
              sensitive
              value={returnDraft.managerPin}
              onUserChange={(value) =>
                setReturnDraft((current) => (current ? { ...current, managerPin: value } : current))
              }
            />
            <div className="dialog-actions">
              <button
                className="button secondary"
                type="button"
                onClick={() => {
                  setReturnDraft(null);
                  onReturnFormReset?.();
                }}
              >
                取消
              </button>
              <button
                className="button primary"
                type="submit"
                disabled={
                  effectiveReturnPending ||
                  anyAddPending ||
                  returnDraft.reason.trim().length < 2 ||
                  returnDraft.managerLogin.trim().length < 1 ||
                  returnDraft.managerPin.length < 6
                }
              >
                确认授权并退 1 件
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
}

type BillingOperationAction = {
  type: string;
  reason?: string;
  managerLogin?: string;
  managerPin?: string;
  startedAt?: string;
};

function localDateTimeInputValue(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function BillingOperationPanel({
  order,
  table,
  operator,
  pending,
  disabled,
  longSessionResolutionDisabled,
  onAction,
}: {
  order: OrderDetail | undefined;
  table: TableSnapshot;
  operator: OperatorIdentity;
  pending: boolean;
  disabled: boolean;
  longSessionResolutionDisabled: boolean;
  onAction: (action: BillingOperationAction) => void;
}): React.JSX.Element {
  const billingFieldId = useId();
  const [billingReason, setBillingReason] = useState("");
  const [startedAt, setStartedAt] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjustLogin, setAdjustLogin] = useState("");
  const [adjustPin, setAdjustPin] = useState("");
  const [voidReason, setVoidReason] = useState("");
  const [voidLogin, setVoidLogin] = useState("");
  const [voidPin, setVoidPin] = useState("");
  const [longSessionReason, setLongSessionReason] = useState("");
  const [longSessionLogin, setLongSessionLogin] = useState("");
  const [longSessionPassword, setLongSessionPassword] = useState("");
  useEffect(() => {
    if (order?.startedAt) setStartedAt(localDateTimeInputValue(order.startedAt));
  }, [order?.startedAt]);
  const billingState = order?.billingState ?? "RUNNING";
  const billingReasonValid = billingReason.trim().length >= 2;
  const adjustmentValid =
    startedAt.length > 0 &&
    adjustReason.trim().length >= 2 &&
    adjustLogin.trim().length > 0 &&
    adjustPin.length >= 8;
  const voidValid =
    voidReason.trim().length >= 2 && voidLogin.trim().length > 0 && voidPin.length >= 8;
  const canResolveLongSession = operator.role === "OWNER" || operator.role === "MANAGER";
  const longSessionResolutionValid =
    canResolveLongSession &&
    longSessionReason.trim().length >= 8 &&
    longSessionLogin.trim().length > 0 &&
    longSessionPassword.length >= 8;
  return (
    <div className="billing-operation-panel">
      <div className="billing-state-card">
        <span>
          当前状态
          <strong>
            {billingState === "PAUSED"
              ? "计费已暂停"
              : billingState === "ENDED"
                ? "计费已停止"
                : "正在计费"}
          </strong>
        </span>
        <small>
          暂停期间{order?.priceSnapshot.pauseBillingMode === "FREE" ? "不计费" : "继续计费"}
          ；本单使用
          {order?.priceSnapshot.name ?? "已锁定价格"} v{order?.priceSnapshot.version ?? "-"}。
        </small>
      </div>
      <BillingBreakdownView order={order} />
      {table.sessionRiskLevel === "WARNING" || table.sessionRiskLevel === "CRITICAL" ? (
        <div className="billing-action-card long-session-resolution-card">
          <strong>
            {table.sessionRiskLevel === "CRITICAL" ? "严重超长台局处置" : "超长台局核对"}
          </strong>
          <p>
            原开台{" "}
            {table.sessionStartedAt
              ? new Date(table.sessionStartedAt).toLocaleString("zh-CN")
              : "未知"}
            ，已持续 {formatElapsedSeconds(table.sessionElapsedSeconds ?? 0)}，当前总计
            {formatFee(table.currentTotalFen ?? order?.totalFen ?? table.estimatedFeeFen)}。
          </p>
          <p>
            不知道真实结束时间时，系统不会编造时间：此操作按当前时刻停止继续计费，把原订单转入“异常待核对”，并释放球台供营业使用。
          </p>
          {canResolveLongSession ? (
            <>
              <label>
                核对依据和处理原因
                <input
                  name="business-long-session-resolution-reason"
                  autoComplete="off"
                  value={longSessionReason}
                  onChange={(event) => setLongSessionReason(event.target.value)}
                  placeholder="例如：确认是历史模拟测试遗留，停止继续计费并转异常待核对"
                  maxLength={300}
                />
              </label>
              <label htmlFor={`${billingFieldId}-long-session-authorizer-login`}>
                授权人登录名
                <AutofillSafeInput
                  id={`${billingFieldId}-long-session-authorizer-login`}
                  fieldName="long-session-authorizer-login"
                  value={longSessionLogin}
                  onUserChange={setLongSessionLogin}
                  placeholder="店长或老板登录名"
                />
              </label>
              <label htmlFor={`${billingFieldId}-long-session-authorization`}>
                老板或店长密码
                <AutofillSafeInput
                  id={`${billingFieldId}-long-session-authorization`}
                  fieldName="long-session-authorization"
                  type="password"
                  sensitive
                  value={longSessionPassword}
                  onUserChange={setLongSessionPassword}
                  placeholder="由授权人当场输入（至少 8 位）"
                />
              </label>
              <button
                className="button danger"
                type="button"
                disabled={pending || longSessionResolutionDisabled || !longSessionResolutionValid}
                onClick={() => {
                  const credential = longSessionPassword;
                  setLongSessionPassword("");
                  onAction({
                    type: "RESOLVE_LONG_SESSION",
                    reason: longSessionReason.trim(),
                    managerLogin: longSessionLogin.trim(),
                    managerPin: credential,
                  });
                }}
              >
                二次授权并转异常待核对
              </button>
            </>
          ) : (
            <span className="authorization-note">当前角色只能查看，请通知老板或店长本人处理。</span>
          )}
        </div>
      ) : null}
      <div className="billing-action-card">
        <strong>{billingState === "PAUSED" ? "恢复计费" : "暂停或提前停表"}</strong>
        <label>
          操作原因
          <input
            value={billingReason}
            onChange={(event) => setBillingReason(event.target.value)}
            placeholder="例如：顾客临时离开 / 提前结束打球"
            maxLength={200}
          />
        </label>
        <div className="dialog-actions">
          {billingState === "PAUSED" ? (
            <button
              className="button primary"
              type="button"
              disabled={pending || disabled || !billingReasonValid}
              onClick={() => onAction({ type: "RESUME", reason: billingReason.trim() })}
            >
              恢复计费
            </button>
          ) : billingState === "RUNNING" ? (
            <>
              <button
                className="button secondary"
                type="button"
                disabled={pending || disabled || !billingReasonValid}
                onClick={() => onAction({ type: "PAUSE", reason: billingReason.trim() })}
              >
                暂停计费
              </button>
              <button
                className="button primary"
                type="button"
                disabled={pending || disabled || !billingReasonValid}
                onClick={() => onAction({ type: "END_EARLY", reason: billingReason.trim() })}
              >
                提前停表并冻结台费
              </button>
            </>
          ) : (
            <span className="authorization-note">台费已冻结，可继续核对商品后结账。</span>
          )}
        </div>
      </div>
      <div className="billing-action-card">
        <strong>经理授权修改开台时间</strong>
        <p>用于漏开、晚开等纠错。系统会保留修改前后时间、原因、操作人和授权人。</p>
        <label>
          正确开台时间
          <input
            type="datetime-local"
            value={startedAt}
            max={localDateTimeInputValue(new Date().toISOString())}
            onChange={(event) => setStartedAt(event.target.value)}
          />
        </label>
        <label>
          修改原因
          <input
            value={adjustReason}
            onChange={(event) => setAdjustReason(event.target.value)}
            placeholder="例如：员工漏点开台，按监控时间补录"
            maxLength={200}
          />
        </label>
        <label htmlFor={`${billingFieldId}-adjust-authorizer-login`}>
          授权人登录名
          <AutofillSafeInput
            id={`${billingFieldId}-adjust-authorizer-login`}
            fieldName="billing-adjust-authorizer-login"
            value={adjustLogin}
            onUserChange={setAdjustLogin}
            placeholder="店长或老板登录名"
          />
        </label>
        <label htmlFor={`${billingFieldId}-adjust-authorization`}>
          老板或店长密码
          <AutofillSafeInput
            id={`${billingFieldId}-adjust-authorization`}
            fieldName="billing-adjust-authorization"
            type="password"
            sensitive
            value={adjustPin}
            onUserChange={setAdjustPin}
            placeholder="由授权人当场输入（至少 8 位）"
          />
        </label>
        <button
          className="button secondary"
          type="button"
          disabled={pending || disabled || billingState === "ENDED" || !adjustmentValid}
          onClick={() =>
            onAction({
              type: "ADJUST_START",
              startedAt: new Date(startedAt).toISOString(),
              reason: adjustReason.trim(),
              managerLogin: adjustLogin.trim(),
              managerPin: adjustPin,
            })
          }
        >
          授权并修改开台时间
        </button>
      </div>
      <div className="billing-action-card danger-zone">
        <strong>误开台作废</strong>
        <p>仅用于没有商品、助教服务和收款的误开台；需要老板或店长当场授权。</p>
        <label>
          作废原因
          <input
            value={voidReason}
            onChange={(event) => setVoidReason(event.target.value)}
            placeholder="例如：点错球台"
            maxLength={200}
          />
        </label>
        <label htmlFor={`${billingFieldId}-void-authorizer-login`}>
          授权人登录名
          <AutofillSafeInput
            id={`${billingFieldId}-void-authorizer-login`}
            fieldName="billing-void-authorizer-login"
            value={voidLogin}
            onUserChange={setVoidLogin}
            placeholder="店长或老板登录名"
          />
        </label>
        <label htmlFor={`${billingFieldId}-void-authorization`}>
          老板或店长密码
          <AutofillSafeInput
            id={`${billingFieldId}-void-authorization`}
            fieldName="billing-void-authorization"
            type="password"
            sensitive
            value={voidPin}
            onUserChange={setVoidPin}
            placeholder="由授权人当场输入（至少 8 位）"
          />
        </label>
        <button
          className="button danger"
          type="button"
          disabled={pending || disabled || !voidValid}
          onClick={() =>
            onAction({
              type: "VOID",
              reason: voidReason.trim(),
              managerLogin: voidLogin.trim(),
              managerPin: voidPin,
            })
          }
        >
          授权并作废误开台
        </button>
      </div>
    </div>
  );
}

function BillingBreakdownView({ order }: { order: OrderDetail | undefined }): React.JSX.Element {
  const breakdown = order?.billingBreakdown;
  if (!order) return <div className="compact-empty">正在读取计费明细...</div>;
  return (
    <div className="billing-breakdown">
      <div className="section-heading compact">
        <div>
          <h3>台费怎么算</h3>
          <p>
            {order.priceSnapshot.name} · 当时采用的第 {order.priceSnapshot.version} 次价格设置 ·{" "}
            {order.priceSnapshot.evidence === "SIMULATED" ? "演示规则" : "门店已确认规则"}
          </p>
        </div>
        <strong>{formatFee(order.tableFeeFen)}</strong>
      </div>
      {breakdown?.segments.length ? (
        <div className="billing-segment-list">
          {breakdown.segments.map((segment) => (
            <article key={segment.id} className="billing-segment-row">
              <div>
                <strong>{segment.priceBandName}</strong>
                <small>
                  {new Date(segment.startedAt).toLocaleString("zh-CN")} 至{" "}
                  {new Date(segment.endedAt).toLocaleString("zh-CN")}
                </small>
              </div>
              <div>
                <span>实际 {Math.ceil(segment.elapsedSeconds / 60)} 分钟</span>
                <span>计费 {segment.billedMinutes} 分钟</span>
                <span>单价 {formatFee(segment.hourlyRateFen)}/小时</span>
                <span>
                  {segment.roundingMode === "PACKAGE_FIXED"
                    ? "包时固定价"
                    : `${segment.roundingMinutes} 分钟步长 · ${
                        segment.roundingMode === "UP"
                          ? "向上取整"
                          : segment.roundingMode === "DOWN"
                            ? "向下取整"
                            : "就近取整"
                      }`}
                </span>
                <strong>{formatFee(segment.amountFen)}</strong>
              </div>
            </article>
          ))}
          {breakdown.minimumAmountAdjustmentFen > 0 ? (
            <div className="billing-minimum-row">
              最低消费补足 <strong>{formatFee(breakdown.minimumAmountAdjustmentFen)}</strong>
            </div>
          ) : null}
          <small>
            总时长 {Math.ceil(breakdown.elapsedSeconds / 60)} 分钟 · 暂停{" "}
            {Math.ceil(breakdown.pausedSeconds / 60)} 分钟 · 计费 {breakdown.billedMinutes} 分钟
          </small>
        </div>
      ) : (
        <p className="compact-empty">
          当前暂无可展示的计费分段；系统仍按本单开台时采用的价格计算。
        </p>
      )}
    </div>
  );
}

export function isCheckoutPaymentMethodEnabled(
  candidate: PaymentMethod,
  memberFundsAvailable: boolean,
  manualPaymentMethods: GatewayStatus["manualPaymentMethods"] | undefined,
): boolean {
  const paymentMethods = manualPaymentMethods ?? {
    cash: true,
    staticWechat: true,
    staticAlipay: true,
  };
  return candidate === "MEMBER_BALANCE"
    ? memberFundsAvailable
    : candidate === "CASH"
      ? paymentMethods.cash
      : candidate === "STATIC_WECHAT"
        ? paymentMethods.staticWechat
        : paymentMethods.staticAlipay;
}

export function nextCheckoutMemberIndex(
  current: number,
  key: "ArrowDown" | "ArrowUp" | "Escape" | string,
  resultCount: number,
): number {
  if (key === "Escape" || resultCount <= 0) return -1;
  if (key === "ArrowDown") return current < 0 ? 0 : (current + 1) % resultCount;
  if (key === "ArrowUp") return current <= 0 ? resultCount - 1 : current - 1;
  return current;
}

function checkoutMemberSearchLabel(member: MemberRecord): string {
  return `${member.name} · ${member.memberNumber}`;
}

export function CheckoutMemberPicker({
  search,
  results,
  selected,
  loading,
  error,
  activeIndex,
  scanNotice,
  orderTotalFen,
  onSearch,
  onSelect,
  onActiveIndex,
  onScanPlaceholder,
}: {
  search: string;
  results: MemberRecord[];
  selected: MemberRecord | null;
  loading: boolean;
  error: string | null;
  activeIndex: number;
  scanNotice: string | null;
  orderTotalFen?: number | undefined;
  onSearch: (value: string) => void;
  onSelect: (member: MemberRecord) => void;
  onActiveIndex: (index: number) => void;
  onScanPlaceholder: () => void;
}): React.JSX.Element {
  const inputId = useId();
  const resultListId = useId();
  const visibleResults = results.filter((member) => member.status === "ACTIVE").slice(0, 30);
  const selectedLabel = selected ? checkoutMemberSearchLabel(selected) : null;
  const showResults = search.trim().length > 0 && search !== selectedLabel;

  return (
    <div className="checkout-member-search">
      <label htmlFor={inputId}>搜索会员</label>
      <div className="checkout-member-search-row">
        <span className="checkout-member-search-input">
          <Search size={16} aria-hidden="true" />
          <AutofillSafeInput
            id={inputId}
            fieldName="checkout-member-search"
            type="search"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showResults && visibleResults.length > 0}
            aria-controls={resultListId}
            aria-activedescendant={
              activeIndex >= 0 && activeIndex < visibleResults.length
                ? `${resultListId}-${visibleResults[activeIndex]?.id}`
                : undefined
            }
            value={search}
            placeholder="输入姓名、手机号或会员号"
            onUserChange={onSearch}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                onActiveIndex(
                  nextCheckoutMemberIndex(activeIndex, event.key, visibleResults.length),
                );
                return;
              }
              if (event.key === "Escape") {
                onActiveIndex(-1);
                return;
              }
              if (event.key === "Enter" && activeIndex >= 0) {
                const member = visibleResults[activeIndex];
                if (member) {
                  event.preventDefault();
                  onSelect(member);
                }
              }
            }}
          />
        </span>
        <button className="button secondary compact" type="button" onClick={onScanPlaceholder}>
          扫码 / 读卡
        </button>
      </div>
      {scanNotice ? <p className="checkout-member-scan-note">{scanNotice}</p> : null}
      {loading ? <output className="checkout-member-query-state">正在搜索会员…</output> : null}
      {error ? (
        <div className="checkout-member-query-state is-error" role="alert">
          会员搜索失败：{error}。请检查网络后继续输入原条件重试。
        </div>
      ) : null}
      {showResults && !loading && !error && visibleResults.length === 0 ? (
        <p className="checkout-member-query-state">没有找到会员，请核对姓名、手机号或会员号。</p>
      ) : null}
      {showResults && visibleResults.length > 0 ? (
        <div className="checkout-member-results" id={resultListId} role="listbox">
          {visibleResults.map((member, index) => (
            <button
              id={`${resultListId}-${member.id}`}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              className={index === activeIndex ? "is-active" : ""}
              key={member.id}
              onMouseEnter={() => onActiveIndex(index)}
              onClick={() => onSelect(member)}
            >
              <span>
                <strong>{member.name}</strong>
                <small>
                  {member.memberNumber} · {member.phoneMasked}
                </small>
              </span>
              <b>{formatFee(member.availableBalanceFen)}</b>
            </button>
          ))}
          {results.length > visibleResults.length ? (
            <small className="checkout-member-result-limit">
              当前只显示前 30 条，请继续输入手机号或会员号缩小范围。
            </small>
          ) : null}
        </div>
      ) : null}
      {selected ? (
        <section className="checkout-member-confirmation" aria-label="已选择会员，请再次核对">
          <div>
            <small>已选择会员，请再次核对</small>
            <strong>{selected.name}</strong>
            <span>
              {selected.memberNumber} · {selected.phoneMasked}
            </span>
          </div>
          <div>
            <small>可用余额</small>
            <strong>{formatFee(selected.availableBalanceFen)}</strong>
            <span>本次应收 {formatFee(orderTotalFen ?? 0)}</span>
          </div>
          <p>本次扣款前请核对会员与应收金额；确认结账后同一请求不会重复扣款。</p>
        </section>
      ) : null}
    </div>
  );
}

function CheckoutPanel({
  method,
  onMethod,
  order,
  pending,
  disabled,
  memberFundsAvailable,
  manualPaymentMethods,
  onCheckout,
}: {
  method: PaymentMethod;
  onMethod: (method: PaymentMethod) => void;
  order: OrderDetail | undefined;
  pending: boolean;
  disabled: boolean;
  memberFundsAvailable: boolean;
  manualPaymentMethods: GatewayStatus["manualPaymentMethods"] | undefined;
  onCheckout: (member?: { memberId: string; memberBalanceFen?: number }) => void;
}): React.JSX.Element {
  const [useMixedBalance, setUseMixedBalance] = useState(false);
  const [mixedBalanceYuan, setMixedBalanceYuan] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState("");
  const [selectedMember, setSelectedMember] = useState<MemberRecord | null>(null);
  const [activeMemberIndex, setActiveMemberIndex] = useState(-1);
  const [memberScanNotice, setMemberScanNotice] = useState<string | null>(null);
  const zeroAmount = order?.totalFen === 0;
  const memberOnly = method === "MEMBER_BALANCE";
  const memberPayment = !zeroAmount && (memberOnly || useMixedBalance);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedMemberSearch(memberSearch.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [memberSearch]);
  const members = useQuery({
    queryKey: ["checkout-members", debouncedMemberSearch],
    queryFn: () => listMembers(debouncedMemberSearch),
    enabled: memberPayment && debouncedMemberSearch.length > 0,
    retry: false,
  });
  const mixedBalanceFen = Math.round(Number(mixedBalanceYuan || "0") * 100);
  const staticCode = method === "STATIC_WECHAT" || method === "STATIC_ALIPAY";
  const isMethodEnabled = (candidate: PaymentMethod): boolean =>
    isCheckoutPaymentMethodEnabled(candidate, memberFundsAvailable, manualPaymentMethods);
  const selectedMethodEnabled = zeroAmount || isMethodEnabled(method);
  const memberAmountValid = isMemberPaymentAmountValid({
    selectedMember: selectedMember ?? undefined,
    orderTotalFen: order?.totalFen,
    memberOnly,
    useMixedBalance,
    mixedBalanceFen,
  });
  const paymentPlan = memberCheckoutPlan({
    method,
    orderTotalFen: order?.totalFen,
    memberOnly,
    useMixedBalance,
    mixedBalanceFen,
  });
  const paymentGroupName = useId();
  return (
    <div className="checkout-panel">
      <BillingBreakdownView order={order} />
      <fieldset className="payment-methods" disabled={zeroAmount}>
        <legend className="visually-hidden">收款方式</legend>
        {(["CASH", "STATIC_WECHAT", "STATIC_ALIPAY", "MEMBER_BALANCE"] as PaymentMethod[]).map(
          (candidate) => (
            <label
              className={method === candidate ? "is-active" : ""}
              title={isMethodEnabled(candidate) ? undefined : "该收款方式已在门店经营设置中停用"}
              key={candidate}
            >
              <input
                type="radio"
                name={paymentGroupName}
                value={candidate}
                checked={method === candidate}
                disabled={!isMethodEnabled(candidate)}
                onChange={() => {
                  onMethod(candidate);
                  if (candidate === "MEMBER_BALANCE") setUseMixedBalance(false);
                }}
              />
              {candidate === "CASH" ? <Banknote size={17} /> : <CircleDollarSign size={17} />}
              {paymentLabels[candidate]}
              {!isMethodEnabled(candidate) ? (
                <small>{candidate === "MEMBER_BALANCE" ? "需要联网" : "门店已停用"}</small>
              ) : null}
            </label>
          ),
        )}
      </fieldset>
      {!zeroAmount && memberFundsAvailable && method !== "MEMBER_BALANCE" ? (
        <label className="member-mix-toggle">
          <input
            type="checkbox"
            checked={useMixedBalance}
            onChange={(event) => setUseMixedBalance(event.target.checked)}
          />
          <span>
            <strong>先用一部分会员余额，再收剩余{method === "CASH" ? "现金" : "静态码款"}</strong>
            <small>
              开启：你要填写本次抵扣的会员余额，剩余金额用当前收款方式收取。关闭：本单全部使用当前收款方式，不扣会员余额。
            </small>
          </span>
        </label>
      ) : null}
      {memberPayment ? (
        <div className="checkout-member-panel">
          <CheckoutMemberPicker
            search={memberSearch}
            results={members.data ?? []}
            selected={selectedMember}
            loading={members.isFetching}
            error={
              members.isError
                ? members.error instanceof Error
                  ? members.error.message
                  : "系统没有返回明确原因"
                : null
            }
            activeIndex={activeMemberIndex}
            scanNotice={memberScanNotice}
            orderTotalFen={order?.totalFen}
            onSearch={(value) => {
              setMemberSearch(value);
              setActiveMemberIndex(-1);
              setMemberScanNotice(null);
              if (selectedMember && value !== checkoutMemberSearchLabel(selectedMember)) {
                setSelectedMember(null);
              }
            }}
            onSelect={(member) => {
              setSelectedMember(member);
              setMemberSearch(checkoutMemberSearchLabel(member));
              setActiveMemberIndex(-1);
            }}
            onActiveIndex={setActiveMemberIndex}
            onScanPlaceholder={() =>
              setMemberScanNotice(
                "扫码或读卡入口已经预留；本轮尚未连接真实设备，请继续用姓名、手机号或会员号搜索。",
              )
            }
          />
          {useMixedBalance ? (
            <label>
              本次余额抵扣（元）
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={mixedBalanceYuan}
                onChange={(event) => setMixedBalanceYuan(event.target.value)}
                placeholder="必须小于本单应收"
              />
            </label>
          ) : null}
          {selectedMember ? (
            <p>
              现金单位钱包合计 {formatFee(selectedMember.availableBalanceFen)}
              ；积分、次数或时长等钱包由中心账本按本单商品、台费和助教规则换算。断网不会扣款。
            </p>
          ) : null}
          {!memberAmountValid && selectedMember ? (
            <div className="disabled-reason">
              组合付款的钱包抵扣金额必须大于 0 且小于本单应收，请修改后再确认收款。
            </div>
          ) : null}
        </div>
      ) : null}
      {memberPayment && selectedMember && memberAmountValid && paymentPlan ? (
        <CheckoutPaymentPlan plan={paymentPlan} />
      ) : null}
      <div className={`payment-notice ${staticCode ? "is-pending" : ""}`}>
        {zeroAmount ? (
          <>
            <Check size={18} />
            <span>
              <strong>团购券已覆盖本单应收</strong>
              <small>无需收现金、扫码或扣会员钱包，确认后直接结账并进入待清台。</small>
            </span>
          </>
        ) : memberOnly ? (
          <>
            <ShieldCheck size={18} />
            <span>
              <strong>在线核对余额后扣款</strong>
              <small>同一结账请求重试不会重复扣减会员余额。</small>
            </span>
          </>
        ) : staticCode ? (
          <>
            <BadgeAlert size={18} />
            <span>
              <strong>员工人工确认，结账后保持“待渠道核对”</strong>
              <small>系统不会把顾客截图或口头确认伪报为渠道到账。</small>
            </span>
          </>
        ) : (
          <>
            <Check size={18} />
            <span>
              <strong>现金由门店本地确认</strong>
              <small>不依赖第三方渠道，可在受限离线模式完成。</small>
            </span>
          </>
        )}
      </div>
      <div className="checkout-total">
        <span>应收</span>
        <strong>{formatFee(order?.totalFen ?? 0)}</strong>
      </div>
      <button
        className="button primary checkout-button"
        type="button"
        disabled={
          pending ||
          disabled ||
          !order ||
          !selectedMethodEnabled ||
          (!zeroAmount && (!memberAmountValid || (memberPayment && !selectedMember)))
        }
        onClick={() =>
          onCheckout(
            memberPayment && selectedMember
              ? {
                  memberId: selectedMember.id,
                  ...(useMixedBalance ? { memberBalanceFen: mixedBalanceFen } : {}),
                }
              : undefined,
          )
        }
      >
        <ReceiptText size={17} />
        {zeroAmount ? "确认无需补收、结账并关台" : "确认收款、结账并关台"}
      </button>
    </div>
  );
}

export interface OrderSearchDraft {
  dateFrom: string;
  dateTo: string;
  orderQuery: string;
  tableNumber: string;
  memberSearch: string;
  paymentMethod: PaymentMethod | "";
}

export function orderSearchDraftError(draft: OrderSearchDraft): string | null {
  if (draft.dateFrom && draft.dateTo && draft.dateFrom > draft.dateTo) {
    return "开始日期不能晚于结束日期";
  }
  if (draft.orderQuery.trim().length > 128) return "订单号搜索不能超过 128 个字符";
  if (draft.tableNumber.trim().length > 32) return "桌号搜索不能超过 32 个字符";
  if (draft.memberSearch.trim().length > 80) return "会员搜索不能超过 80 个字符";
  return null;
}

function OrdersDesk(): React.JSX.Element {
  const queryClient = useQueryClient();
  const refundFieldId = useId();
  const [offlineOnly, setOfflineOnly] = useState(false);
  const [searchDraft, setSearchDraft] = useState<OrderSearchDraft>({
    dateFrom: "",
    dateTo: "",
    orderQuery: "",
    tableNumber: "",
    memberSearch: "",
    paymentMethod: "",
  });
  const [appliedSearch, setAppliedSearch] = useState<OrderSearchDraft>(searchDraft);
  const orderSearchInitialized = useRef(false);
  const [refundDraft, setRefundDraft] = useState<{
    order: OrderDetail;
    amountYuan: string;
    reason: string;
    managerLogin: string;
    managerPin: string;
  } | null>(null);
  const [printNotice, setPrintNotice] = useState("");
  const summary = useQuery({
    queryKey: ["reconciliation", "current-business-day"],
    queryFn: () => getReconciliationSummary(),
    refetchInterval: 2_000,
  });
  useEffect(() => {
    if (!summary.data?.businessDate || orderSearchInitialized.current) return;
    orderSearchInitialized.current = true;
    const initialSearch: OrderSearchDraft = {
      dateFrom: summary.data.businessDate,
      dateTo: summary.data.businessDate,
      orderQuery: "",
      tableNumber: "",
      memberSearch: "",
      paymentMethod: "",
    };
    setSearchDraft(initialSearch);
    setAppliedSearch(initialSearch);
  }, [summary.data?.businessDate]);
  const orders = useQuery({
    queryKey: ["orders", offlineOnly, appliedSearch],
    queryFn: () =>
      listOrders({
        offlineOnly,
        dateFrom: appliedSearch.dateFrom,
        dateTo: appliedSearch.dateTo,
        orderQuery: appliedSearch.orderQuery.trim(),
        tableNumber: appliedSearch.tableNumber.trim(),
        memberSearch: appliedSearch.memberSearch.trim(),
        paymentMethod: appliedSearch.paymentMethod,
      }),
    enabled: orderSearchInitialized.current,
    refetchInterval: 2_000,
  });
  const connectivity = useQuery({
    queryKey: ["connectivity"],
    queryFn: getConnectivity,
    refetchInterval: 1_000,
  });
  const refund = useMutation({
    mutationFn: async (draft: NonNullable<typeof refundDraft>) => {
      const amountFen = Math.round(Number(draft.amountYuan) * 100);
      const authorization = await requestSecondaryAuthorization(
        "REFUND_ORDER",
        draft.managerPin,
        draft.managerLogin,
      );
      return runIdempotentOperation(
        `refund:${draft.order.id}:${amountFen}:${draft.reason.trim()}`,
        (idempotencyKey) =>
          refundOrder(
            draft.order.id,
            amountFen,
            draft.reason.trim(),
            idempotencyKey,
            authorization.token,
          ),
      );
    },
    onSuccess: async () => {
      setRefundDraft(null);
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({ queryKey: ["reconciliation"] });
    },
  });
  const reprint = useMutation({
    mutationFn: (orderId: string) =>
      runIdempotentOperation(`reprint:${orderId}`, (idempotencyKey) =>
        reprintReceipt(orderId, idempotencyKey),
      ),
    onSuccess: async (effect) => {
      setPrintNotice(
        `小票已交给${effect.evidence === "MOCK" ? "测试" : "门店"}打印设备；失败或结果未知会持续显示人工处理提醒。`,
      );
      await queryClient.invalidateQueries({ queryKey: ["device-status"] });
    },
  });
  const online =
    hasFreshConnectivity(connectivity) &&
    connectivity.data?.mode === "ONLINE" &&
    connectivity.data.serviceHealth.onlineRefund;
  const summaryCount = (value: number | undefined): number | string =>
    summary.isError ? "不可用" : summary.isPending ? "…" : (value ?? 0);
  const summaryMoney = (value: number | undefined): string =>
    summary.isError ? "不可用" : summary.isPending ? "…" : formatFee(value ?? 0);
  const searchError = orderSearchDraftError(searchDraft);
  return (
    <>
      <PageHeader
        eyebrow={`账单与日结 · 营业日 ${summary.data?.businessDate ?? "读取中"}`}
        title="订单核账"
        action={
          <div className="segmented">
            <button
              type="button"
              className={!offlineOnly ? "is-active" : ""}
              onClick={() => setOfflineOnly(false)}
            >
              全部
            </button>
            <button
              type="button"
              className={offlineOnly ? "is-active" : ""}
              onClick={() => setOfflineOnly(true)}
            >
              离线订单
            </button>
          </div>
        }
      />
      <section className="reconciliation-strip">
        <div>
          <span>营业日</span>
          <strong>{summary.isError ? "不可用" : (summary.data?.businessDate ?? "…")}</strong>
        </div>
        <div>
          <span>订单</span>
          <strong>{summaryCount(summary.data?.orders)}</strong>
        </div>
        <div>
          <span>离线产生</span>
          <strong>{summaryCount(summary.data?.offlineOrders)}</strong>
        </div>
        <div>
          <span>订单原始金额</span>
          <strong>{summaryMoney(summary.data?.originalAmountFen)}</strong>
        </div>
        <div>
          <span>优惠/调整</span>
          <strong>{summaryMoney(summary.data?.adjustmentsFen)}</strong>
          <small>{summaryCount(summary.data?.adjustmentRecords)} 笔</small>
        </div>
        <div>
          <span>系统应收</span>
          <strong>{summaryMoney(summary.data?.receivableFen)}</strong>
        </div>
        <div>
          <span>已确认实收</span>
          <strong>{summaryMoney(summary.data?.settledFen)}</strong>
        </div>
        <div>
          <span>现金</span>
          <strong>{summaryMoney(summary.data?.cashFen)}</strong>
        </div>
        <div>
          <span>微信已确认</span>
          <strong>{summaryMoney(summary.data?.staticWechatFen)}</strong>
        </div>
        <div>
          <span>支付宝已确认</span>
          <strong>{summaryMoney(summary.data?.staticAlipayFen)}</strong>
        </div>
        <div>
          <span>未结订单</span>
          <strong>{summaryMoney(summary.data?.unsettledFen)}</strong>
          <small>{summaryCount(summary.data?.unsettledOrders)} 笔</small>
        </div>
        <div>
          <span>核对异常</span>
          <strong>{summaryMoney(summary.data?.exceptionReconciliationFen)}</strong>
        </div>
        <div>
          <span>退款</span>
          <strong>{summaryMoney(summary.data?.refundedFen)}</strong>
          <small>
            现金 {summaryMoney(summary.data?.cashRefundedFen)} · 微信{" "}
            {summaryMoney(summary.data?.staticWechatRefundedFen)} · 支付宝{" "}
            {summaryMoney(summary.data?.staticAlipayRefundedFen)}
          </small>
        </div>
        <div>
          <span>待渠道核对</span>
          <strong>{summaryMoney(summary.data?.pendingReconciliationFen)}</strong>
        </div>
        <div>
          <span>未解释差异</span>
          <strong>{summaryMoney(summary.data?.unexplainedDifferenceFen)}</strong>
        </div>
        <div>
          <span>冲突</span>
          <strong>{summaryCount(summary.data?.conflicts)}</strong>
        </div>
        <span className="evidence-label">演示账目</span>
      </section>
      <form
        className="order-search-panel"
        name="order-search"
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          if (searchError) return;
          setAppliedSearch(searchDraft);
        }}
      >
        <div className="order-search-heading">
          <div>
            <strong>查找订单</strong>
            <small>可组合日期、订单号、桌号、会员和收款方式；会员姓名或手机号搜索需要联网。</small>
          </div>
          <span>{orders.isFetching ? "正在查找…" : `找到 ${orders.data?.length ?? 0} 笔`}</span>
        </div>
        <label>
          开始日期
          <input
            type="date"
            name="order-search-date-from"
            value={searchDraft.dateFrom}
            onChange={(event) =>
              setSearchDraft((current) => ({ ...current, dateFrom: event.target.value }))
            }
          />
        </label>
        <label>
          结束日期
          <input
            type="date"
            name="order-search-date-to"
            value={searchDraft.dateTo}
            onChange={(event) =>
              setSearchDraft((current) => ({ ...current, dateTo: event.target.value }))
            }
          />
        </label>
        <label>
          订单号
          <input
            name="order-search-order-number"
            autoComplete="off"
            value={searchDraft.orderQuery}
            onChange={(event) =>
              setSearchDraft((current) => ({ ...current, orderQuery: event.target.value }))
            }
            maxLength={128}
            placeholder="可输入完整或部分订单号"
          />
        </label>
        <label>
          桌号
          <input
            name="order-search-table-number"
            autoComplete="off"
            value={searchDraft.tableNumber}
            onChange={(event) =>
              setSearchDraft((current) => ({ ...current, tableNumber: event.target.value }))
            }
            maxLength={32}
            placeholder="例如：01"
          />
        </label>
        <label>
          会员
          <input
            name="order-search-member"
            autoComplete="off"
            value={searchDraft.memberSearch}
            onChange={(event) =>
              setSearchDraft((current) => ({ ...current, memberSearch: event.target.value }))
            }
            maxLength={80}
            placeholder="姓名、手机号或会员号"
          />
        </label>
        <label>
          收款方式
          <select
            name="order-search-payment-method"
            value={searchDraft.paymentMethod}
            onChange={(event) =>
              setSearchDraft((current) => ({
                ...current,
                paymentMethod: event.target.value as PaymentMethod | "",
              }))
            }
          >
            <option value="">全部方式</option>
            <option value="CASH">现金</option>
            <option value="STATIC_WECHAT">微信静态码</option>
            <option value="STATIC_ALIPAY">支付宝静态码</option>
            <option value="MEMBER_BALANCE">会员余额</option>
          </select>
        </label>
        {searchError ? <div className="form-hint">还需修改：{searchError}</div> : null}
        <div className="order-search-actions">
          <button
            className="button secondary"
            type="button"
            onClick={() => {
              const cleared: OrderSearchDraft = {
                dateFrom: summary.data?.businessDate ?? "",
                dateTo: summary.data?.businessDate ?? "",
                orderQuery: "",
                tableNumber: "",
                memberSearch: "",
                paymentMethod: "",
              };
              setSearchDraft(cleared);
              setAppliedSearch(cleared);
            }}
          >
            清空条件
          </button>
          <button className="button primary" type="submit" disabled={Boolean(searchError)}>
            <Search size={16} aria-hidden="true" /> 查找订单
          </button>
        </div>
      </form>
      <section className="order-list">
        {summary.isError ? (
          <div className="error-band">
            日结汇总不可用：营业日设置有误，或门店服务尚未完成经营设置同步
          </div>
        ) : null}
        {orders.isError ? <div className="error-band">订单读取失败</div> : null}
        {refund.error instanceof Error ? (
          <div className="error-band">退款未提交：{refund.error.message}</div>
        ) : null}
        {reprint.error instanceof Error ? (
          <div className="error-band">小票未能提交打印：{reprint.error.message}</div>
        ) : printNotice ? (
          <div className="success-band">{printNotice}</div>
        ) : null}
        {orders.isPending || (summary.isPending && !summary.isError) ? (
          <div className="loading-state">正在读取当日订单...</div>
        ) : orders.data?.length ? (
          orders.data.map((order) => {
            const refundable =
              order.status !== "CONFLICT" &&
              order.payments.some(
                (payment) =>
                  payment.status === "CONFIRMED_LOCAL" ||
                  payment.status === "CONFIRMED_CLOUD" ||
                  payment.status === "MANUALLY_RECONCILED" ||
                  payment.status === "PARTIALLY_REFUNDED",
              );
            const refundReason = !online
              ? "网络与同步健康检查通过后才可退款"
              : !refundable
                ? "付款仍待渠道核对或存在核对异常，禁止退款"
                : "需要填写金额和原因，并由经理或老板本人二次验证";
            return (
              <article className="order-row" key={order.id}>
                <div className="order-main">
                  <span className="order-table">{order.tableNumber}</span>
                  <div>
                    <strong>
                      {formatFee(order.totalFen)} · {orderStatusLabels[order.status]}
                    </strong>
                    <small>
                      营业日 {order.businessDate} ·{" "}
                      {new Date(order.startedAt).toLocaleString("zh-CN", { hour12: false })} ·{" "}
                      {order.offlineOrigin ? "离线期间产生" : "在线产生"} ·{" "}
                      {syncStateLabels[order.syncState]}
                    </small>
                    <small className="order-number">订单号 {order.id}</small>
                  </div>
                </div>
                <OrderBreakdown order={order} />
                <div className="payment-records">
                  {order.payments.length ? (
                    order.payments.map((payment) => (
                      <span
                        className={`payment-tag status-${payment.status.toLowerCase()}`}
                        key={payment.id}
                      >
                        {paymentLabels[payment.method]} · {paymentStatusLabels[payment.status]}
                      </span>
                    ))
                  ) : (
                    <span className="payment-tag">尚未收款</span>
                  )}
                </div>
                {order.refunds.length ? (
                  <div className="refund-records">
                    {order.refunds.map((record) => (
                      <small key={record.id}>
                        退款 {formatFee(record.amountFen)} · {record.reason} · 授权人{" "}
                        {record.authorizerId} ·{" "}
                        {new Date(record.createdAt).toLocaleString("zh-CN", { hour12: false })}
                      </small>
                    ))}
                  </div>
                ) : null}
                {order.status !== "OPEN" && order.paidFen > order.refundedFen ? (
                  <button
                    className="button secondary refund-button"
                    type="button"
                    disabled={!online || !refundable || refund.isPending}
                    title={refundReason}
                    onClick={() =>
                      setRefundDraft({
                        order,
                        amountYuan: ((order.paidFen - order.refundedFen) / 100).toFixed(2),
                        reason: "",
                        managerLogin: "",
                        managerPin: "",
                      })
                    }
                  >
                    <RotateCcw size={15} />
                    {!online
                      ? "退款不可用：需要联网"
                      : refundable
                        ? "授权退款"
                        : "退款不可用：待核对"}
                  </button>
                ) : null}
                {order.status !== "OPEN" && order.status !== "CONFLICT" ? (
                  <button
                    className="button secondary refund-button"
                    type="button"
                    disabled={reprint.isPending}
                    onClick={() => reprint.mutate(order.id)}
                  >
                    <ReceiptText size={15} />
                    重新打印小票
                  </button>
                ) : null}
              </article>
            );
          })
        ) : (
          <div className="empty-state">当前筛选没有订单</div>
        )}
      </section>
      <CspModal
        open={refundDraft !== null}
        onOpenChange={(open) => !open && setRefundDraft(null)}
        labelledBy={`${refundFieldId}-title`}
        describedBy={`${refundFieldId}-description`}
        className="compact-dialog"
      >
        <h2 id={`${refundFieldId}-title`}>授权退款</h2>
        <p id={`${refundFieldId}-description`}>
          原支付事实保持不变；退款将作为独立反向事实写入账本与审计。
        </p>
        {refund.error instanceof Error ? (
          <div className="dialog-error">{refund.error.message}</div>
        ) : null}
        <form
          name="order-refund-authorization"
          autoComplete="off"
          onSubmit={(event) => {
            event.preventDefault();
            if (refundDraft) refund.mutate(refundDraft);
          }}
        >
          <label className="field-stack">
            <span>退款金额（元）</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={refundDraft?.amountYuan ?? ""}
              onChange={(event) =>
                setRefundDraft((current) =>
                  current ? { ...current, amountYuan: event.target.value } : current,
                )
              }
            />
          </label>
          <label className="field-stack" htmlFor={`${refundFieldId}-reason`}>
            <span>退款原因</span>
            <AutofillSafeInput
              id={`${refundFieldId}-reason`}
              fieldName="order-refund-reason"
              value={refundDraft?.reason ?? ""}
              onUserChange={(value) =>
                setRefundDraft((current) => (current ? { ...current, reason: value } : current))
              }
            />
          </label>
          <label className="field-stack" htmlFor={`${refundFieldId}-authorizer-login`}>
            <span>授权人登录名</span>
            <AutofillSafeInput
              id={`${refundFieldId}-authorizer-login`}
              fieldName="order-refund-authorizer-login"
              value={refundDraft?.managerLogin ?? ""}
              onUserChange={(value) =>
                setRefundDraft((current) =>
                  current ? { ...current, managerLogin: value } : current,
                )
              }
            />
          </label>
          <label className="field-stack" htmlFor={`${refundFieldId}-authorization`}>
            <span>经理密码 / 演示授权码</span>
            <AutofillSafeInput
              id={`${refundFieldId}-authorization`}
              fieldName="order-refund-authorization"
              type="password"
              sensitive
              value={refundDraft?.managerPin ?? ""}
              onUserChange={(value) =>
                setRefundDraft((current) => (current ? { ...current, managerPin: value } : current))
              }
            />
          </label>
          <div className="dialog-actions">
            <button className="button secondary" type="button" onClick={() => setRefundDraft(null)}>
              取消
            </button>
            <button
              className="button primary"
              type="submit"
              disabled={
                !refundDraft ||
                refund.isPending ||
                !Number.isFinite(Number(refundDraft.amountYuan)) ||
                Math.round(Number(refundDraft.amountYuan) * 100) < 1 ||
                Math.round(Number(refundDraft.amountYuan) * 100) >
                  refundDraft.order.paidFen - refundDraft.order.refundedFen ||
                refundDraft.reason.trim().length < 2 ||
                refundDraft.managerLogin.trim().length < 1 ||
                refundDraft.managerPin.length < 6
              }
            >
              确认授权并退款
            </button>
          </div>
        </form>
      </CspModal>
    </>
  );
}

function ReviewDesk(): React.JSX.Element {
  const queryClient = useQueryClient();
  const paymentCredentialRef = useRef("");
  const conflictCredentialRef = useRef("");
  const coachConflictCredentialRef = useRef("");
  const paymentSubmissionRef = useRef(false);
  const conflictSubmissionRef = useRef(false);
  const coachConflictSubmissionRef = useRef(false);
  const connectivity = useQuery({
    queryKey: ["connectivity"],
    queryFn: getConnectivity,
    refetchInterval: 1_000,
  });
  const query = useQuery({
    queryKey: ["manual-review"],
    queryFn: listManualReview,
    refetchInterval: 2_000,
  });
  const [drafts, setDrafts] = useState<
    Record<
      string,
      {
        outcome:
          | ""
          | "MANUALLY_RECONCILED"
          | "NOT_RECEIVED"
          | "AMOUNT_MISMATCH"
          | "DUPLICATE_PAYMENT";
        disposition: "KEEP_ISOLATED" | "TRANSFER_TO_FINANCE" | "TRANSFER_TO_OPERATIONS";
        note: string;
        managerPin: string;
      }
    >
  >({});
  const mutation = useMutation({
    networkMode: "always",
    mutationFn: async ({
      item,
      outcome,
      note,
    }: {
      item: ManualReviewItem;
      outcome: "MANUALLY_RECONCILED" | "NOT_RECEIVED" | "AMOUNT_MISMATCH" | "DUPLICATE_PAYMENT";
      note: string;
    }) => {
      let managerPin = takeSensitiveCredential(paymentCredentialRef);
      if (!managerPin) throw new Error("经理授权凭证已清空，请重新输入后提交");
      let authorization: Awaited<ReturnType<typeof requestSecondaryAuthorization>>;
      try {
        authorization = await requestSecondaryAuthorization("RECONCILE_PAYMENT", managerPin);
      } finally {
        managerPin = "";
      }
      return runIdempotentOperation(
        `reconcile:${item.id}:${outcome}:${note.trim()}`,
        (idempotencyKey) =>
          reconcilePayment(item.id, outcome, note.trim(), idempotencyKey, authorization.token),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["manual-review"] });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onSettled: () => {
      paymentCredentialRef.current = "";
      paymentSubmissionRef.current = false;
    },
  });
  const conflictMutation = useMutation({
    networkMode: "always",
    mutationFn: async ({
      item,
      note,
      disposition,
    }: {
      item: ManualReviewItem;
      note: string;
      disposition: "KEEP_ISOLATED" | "TRANSFER_TO_FINANCE";
    }) => {
      let managerPin = takeSensitiveCredential(conflictCredentialRef);
      if (!managerPin) throw new Error("经理授权凭证已清空，请重新输入后提交");
      let authorization: Awaited<ReturnType<typeof requestSecondaryAuthorization>>;
      try {
        authorization = await requestSecondaryAuthorization("RESOLVE_ORDER_CONFLICT", managerPin);
      } finally {
        managerPin = "";
      }
      return runIdempotentOperation(
        `resolve-conflict:${item.id}:${disposition}:${note.trim()}`,
        (idempotencyKey) =>
          resolveOrderConflict(
            item.id,
            note.trim(),
            idempotencyKey,
            disposition,
            authorization.token,
          ),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["manual-review"] });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({ queryKey: ["connectivity"] });
    },
    onSettled: () => {
      conflictCredentialRef.current = "";
      conflictSubmissionRef.current = false;
    },
  });
  const coachConflictMutation = useMutation({
    networkMode: "always",
    mutationFn: async ({
      item,
      note,
      disposition,
    }: {
      item: ManualReviewItem;
      note: string;
      disposition: "KEEP_ISOLATED" | "TRANSFER_TO_OPERATIONS";
    }) => {
      let managerPin = takeSensitiveCredential(coachConflictCredentialRef);
      if (!managerPin) throw new Error("经理授权凭证已清空，请重新输入后提交");
      let authorization: Awaited<ReturnType<typeof requestSecondaryAuthorization>>;
      try {
        authorization = await requestSecondaryAuthorization("RESOLVE_COACH_CONFLICT", managerPin);
      } finally {
        managerPin = "";
      }
      return runIdempotentOperation(
        `resolve-coach-conflict:${item.id}:${disposition}:${note.trim()}`,
        (idempotencyKey) =>
          resolveCoachConflict(
            item.id,
            note.trim(),
            idempotencyKey,
            disposition,
            authorization.token,
          ),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["manual-review"] });
      await queryClient.invalidateQueries({ queryKey: ["coach-runtime"] });
      await queryClient.invalidateQueries({ queryKey: ["connectivity"] });
    },
    onSettled: () => {
      coachConflictCredentialRef.current = "";
      coachConflictSubmissionRef.current = false;
    },
  });
  const pending = query.data?.filter((item) => item.status === "PENDING") ?? [];
  const freshOnline = hasFreshConnectivity(connectivity) && connectivity.data?.mode === "ONLINE";
  const paymentReconciliationReady =
    freshOnline && connectivity.data?.serviceHealth.paymentReconciliation;
  const conflictResolutionReady =
    hasFreshConnectivity(connectivity) && canResolveOrderConflict(connectivity.data);
  return (
    <>
      <PageHeader
        eyebrow="异常与人工处置"
        title="待人工确认"
        action={<span className="demo-pill">{pending.length} 项待处理</span>}
      />
      <section className="review-list">
        {mutation.error instanceof Error ? (
          <div className="error-band">付款核对未提交：{mutation.error.message}</div>
        ) : null}
        {conflictMutation.error instanceof Error ? (
          <div className="error-band">冲突处置未提交：{conflictMutation.error.message}</div>
        ) : null}
        {coachConflictMutation.error instanceof Error ? (
          <div className="error-band">
            助教事项处置未提交：{coachConflictMutation.error.message}
          </div>
        ) : null}
        {query.isError ? (
          <div className="error-band">待人工确认事项读取失败，请检查门店主机连接后重试</div>
        ) : query.isLoading ? (
          <div className="empty-state">正在读取待人工确认事项</div>
        ) : pending.length ? (
          pending.map((item) => {
            const draft = drafts[item.id] ?? {
              outcome: "" as const,
              disposition: "KEEP_ISOLATED" as const,
              note: "",
              managerPin: "",
            };
            const reconciliationExceptionRecorded =
              item.type === "PAYMENT" &&
              ["NOT_RECEIVED", "AMOUNT_MISMATCH", "DUPLICATE_PAYMENT"].includes(item.reasonCode);
            const orderConflictDispositionRecorded =
              item.type === "ORDER_CONFLICT" &&
              ["ESCALATED_FOR_LEDGER_REVIEW", "ISOLATION_CONFIRMED"].includes(item.reasonCode);
            const coachConflictEscalated =
              item.type === "COACH_CONFLICT" &&
              item.reasonCode === "ESCALATED_FOR_OPERATIONS_REVIEW";
            const context = item.businessContext;
            return (
              <article className="review-row" key={item.id}>
                <div className="review-icon">
                  {item.type === "PAYMENT" ? <CircleDollarSign /> : <AlertTriangle />}
                </div>
                <div className="review-content">
                  <strong>{item.title}</strong>
                  <p>{item.detail}</p>
                  <small>
                    {reviewReasonLabel(item)} · {item.offlineOrigin ? "离线期间产生" : "在线产生"} ·{" "}
                    {item.amountFen === null ? "金额待查" : formatFee(item.amountFen)}
                  </small>
                  {item.type !== "PAYMENT" && context ? (
                    <section className="review-business-context" aria-label="冲突业务上下文">
                      <div className="review-context-heading">
                        <strong>{context.eventLabel}</strong>
                        <code>{context.eventType}</code>
                      </div>
                      <dl>
                        <div>
                          <dt>关联桌号</dt>
                          <dd>{context.tableNumber ?? "未关联桌号"}</dd>
                        </div>
                        <div>
                          <dt>台局编号</dt>
                          <dd>{context.sessionId ?? "未关联台局"}</dd>
                        </div>
                        <div>
                          <dt>订单编号</dt>
                          <dd>{context.orderId}</dd>
                        </div>
                        <div>
                          <dt>营业日</dt>
                          <dd>{context.businessDate ?? "待核对"}</dd>
                        </div>
                        <div>
                          <dt>发生时间</dt>
                          <dd>{context.occurredAt ? formatTime(context.occurredAt) : "待核对"}</dd>
                        </div>
                        <div>
                          <dt>同步时间</dt>
                          <dd>{formatTime(context.synchronizedAt)}</dd>
                        </div>
                        <div>
                          <dt>操作人</dt>
                          <dd>{context.actorId ?? "系统或未知员工"}</dd>
                        </div>
                      </dl>
                      <div className="review-context-explanation">
                        <strong>具体差异</strong>
                        <p>{context.difference}</p>
                      </div>
                      <div className="review-context-explanation is-recommendation">
                        <strong>建议下一步</strong>
                        <p>{context.recommendedAction}</p>
                      </div>
                    </section>
                  ) : null}
                  {item.type === "PAYMENT" ? (
                    <form
                      className="review-form"
                      name={`payment-reconciliation-${item.id}`}
                      autoComplete="off"
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (draft.outcome && !paymentSubmissionRef.current) {
                          paymentSubmissionRef.current = true;
                          paymentCredentialRef.current = draft.managerPin;
                          setDrafts((current) => ({
                            ...current,
                            [item.id]: clearSensitiveDraftCredential(current[item.id] ?? draft),
                          }));
                          mutation.mutate({ item, outcome: draft.outcome, note: draft.note });
                        }
                      }}
                    >
                      {reconciliationExceptionRecorded ? (
                        <div className="disabled-reason review-form-notice">
                          <AlertTriangle size={15} />
                          异常已记录并保持待处理；完成线下处置并确认流水一致后才能关闭。
                        </div>
                      ) : null}
                      <select
                        value={draft.outcome}
                        onChange={(event) =>
                          setDrafts((current) => ({
                            ...current,
                            [item.id]: {
                              ...draft,
                              outcome: event.target.value as typeof draft.outcome,
                              note: "",
                            },
                          }))
                        }
                      >
                        <option value="">请选择核对结论</option>
                        <option value="MANUALLY_RECONCILED">人工流水核对一致</option>
                        {!reconciliationExceptionRecorded ? (
                          <>
                            <option value="NOT_RECEIVED">未到账</option>
                            <option value="AMOUNT_MISMATCH">金额不符</option>
                            <option value="DUPLICATE_PAYMENT">重复付款</option>
                          </>
                        ) : null}
                      </select>
                      <AutofillSafeInput
                        fieldName={`payment-reconciliation-note-${item.id}`}
                        value={draft.note}
                        aria-label="核对说明"
                        placeholder="按所选结论填写核对证据"
                        onUserChange={(value) =>
                          setDrafts((current) => ({
                            ...current,
                            [item.id]: { ...draft, note: value },
                          }))
                        }
                      />
                      <AutofillSafeInput
                        fieldName={`payment-reconciliation-authorization-${item.id}`}
                        value={draft.managerPin}
                        aria-label="经理密码或演示授权码"
                        placeholder="经理密码 / 演示授权码"
                        type="password"
                        sensitive
                        onUserChange={(value) =>
                          setDrafts((current) => ({
                            ...current,
                            [item.id]: { ...draft, managerPin: value },
                          }))
                        }
                      />
                      <button
                        className="button primary"
                        type="submit"
                        disabled={
                          !paymentReconciliationReady ||
                          !draft.outcome ||
                          (reconciliationExceptionRecorded &&
                            draft.outcome !== "MANUALLY_RECONCILED") ||
                          draft.note.trim().length < 2 ||
                          draft.managerPin.length < 6 ||
                          mutation.isPending
                        }
                        title={
                          paymentReconciliationReady
                            ? "经理或老板本人二次验证后提交"
                            : "网络与付款核对服务健康检查通过后才可核对"
                        }
                      >
                        <ListChecks size={16} />
                        {paymentReconciliationReady ? "提交核对" : "核对不可用：服务未恢复"}
                      </button>
                    </form>
                  ) : item.type === "COACH_CONFLICT" ? (
                    <form
                      className="conflict-resolution coach-conflict-resolution"
                      name={`coach-conflict-resolution-${item.id}`}
                      autoComplete="off"
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (coachConflictSubmissionRef.current) return;
                        coachConflictSubmissionRef.current = true;
                        coachConflictCredentialRef.current = draft.managerPin;
                        setDrafts((current) => ({
                          ...current,
                          [item.id]: clearSensitiveDraftCredential(current[item.id] ?? draft),
                        }));
                        coachConflictMutation.mutate({
                          item,
                          note: draft.note,
                          disposition:
                            draft.disposition === "TRANSFER_TO_OPERATIONS"
                              ? "TRANSFER_TO_OPERATIONS"
                              : "KEEP_ISOLATED",
                        });
                      }}
                    >
                      <div className="disabled-reason">
                        <AlertTriangle size={15} />
                        {coachConflictEscalated
                          ? "处理进度：已转交门店运营复核，原始助教事件继续隔离。核对完成后可在这里确认保留隔离。"
                          : "这是助教排队、上下钟或服务记录的同步事项。处置不会改写中心账本已有记录，也不会删除门店原始记录。"}
                      </div>
                      <label className="review-disposition-field">
                        <span>助教事项处理方式</span>
                        <select
                          aria-label="助教同步事项处理方式"
                          name={`business-coach-conflict-disposition-${item.id}`}
                          autoComplete="off"
                          value={
                            draft.disposition === "TRANSFER_TO_OPERATIONS"
                              ? "TRANSFER_TO_OPERATIONS"
                              : "KEEP_ISOLATED"
                          }
                          onChange={(event) =>
                            setDrafts((current) => ({
                              ...current,
                              [item.id]: {
                                ...draft,
                                disposition: event.target.value as
                                  | "KEEP_ISOLATED"
                                  | "TRANSFER_TO_OPERATIONS",
                              },
                            }))
                          }
                        >
                          <option value="KEEP_ISOLATED">核对完成，确认保留隔离</option>
                          <option value="TRANSFER_TO_OPERATIONS">转交门店运营复核</option>
                        </select>
                      </label>
                      <AutofillSafeInput
                        fieldName={`coach-conflict-note-${item.id}`}
                        value={draft.note}
                        aria-label="助教事项核对说明"
                        placeholder="填写助教、桌号、上下钟时间和核对结果"
                        onUserChange={(value) =>
                          setDrafts((current) => ({
                            ...current,
                            [item.id]: { ...draft, note: value },
                          }))
                        }
                      />
                      <AutofillSafeInput
                        fieldName={`coach-conflict-authorization-${item.id}`}
                        value={draft.managerPin}
                        aria-label="经理密码或演示授权码"
                        placeholder="经理密码 / 演示授权码"
                        type="password"
                        sensitive
                        onUserChange={(value) =>
                          setDrafts((current) => ({
                            ...current,
                            [item.id]: { ...draft, managerPin: value },
                          }))
                        }
                      />
                      <button
                        className="button primary"
                        type="submit"
                        disabled={
                          !conflictResolutionReady ||
                          draft.note.trim().length < 2 ||
                          draft.managerPin.length < 6 ||
                          coachConflictMutation.isPending
                        }
                        title={
                          conflictResolutionReady
                            ? draft.disposition === "TRANSFER_TO_OPERATIONS"
                              ? "经理确认后转交门店运营人员继续核对"
                              : "经理确认原始助教事件永久保留隔离"
                            : "云端同步健康检查通过后才可处置"
                        }
                      >
                        <ShieldCheck size={16} />
                        {conflictResolutionReady
                          ? draft.disposition === "TRANSFER_TO_OPERATIONS"
                            ? "转交门店运营复核"
                            : "确认并保留隔离"
                          : "处置不可用：服务未恢复"}
                      </button>
                    </form>
                  ) : (
                    <form
                      className="conflict-resolution"
                      name={`order-conflict-resolution-${item.id}`}
                      autoComplete="off"
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (conflictSubmissionRef.current) return;
                        conflictSubmissionRef.current = true;
                        conflictCredentialRef.current = draft.managerPin;
                        setDrafts((current) => ({
                          ...current,
                          [item.id]: clearSensitiveDraftCredential(current[item.id] ?? draft),
                        }));
                        conflictMutation.mutate({
                          item,
                          note: draft.note,
                          disposition:
                            draft.disposition === "TRANSFER_TO_OPERATIONS"
                              ? "KEEP_ISOLATED"
                              : draft.disposition,
                        });
                      }}
                    >
                      <div className="disabled-reason">
                        <Trash2 size={15} />
                        {item.reasonCode === "ESCALATED_FOR_LEDGER_REVIEW"
                          ? "处理结果：已转交账务复核。原冲突订单与同步记录继续保持隔离。"
                          : item.reasonCode === "ISOLATION_CONFIRMED"
                            ? "处理结果：已确认保留隔离。原冲突未删除；如需继续处理，请由账务人员核对原单据。"
                            : "先选择处理方式。两种处理都不会覆盖本地或云端账目，也不会删除原冲突。"}
                      </div>
                      <label className="review-disposition-field">
                        <span>处理方式</span>
                        <select
                          aria-label="冲突处理方式"
                          name={`business-order-conflict-disposition-${item.id}`}
                          autoComplete="off"
                          value={draft.disposition}
                          disabled={orderConflictDispositionRecorded}
                          onChange={(event) =>
                            setDrafts((current) => ({
                              ...current,
                              [item.id]: {
                                ...draft,
                                disposition: event.target.value as typeof draft.disposition,
                              },
                            }))
                          }
                        >
                          <option value="KEEP_ISOLATED">确认并保留隔离</option>
                          <option value="TRANSFER_TO_FINANCE">转交账务复核</option>
                        </select>
                      </label>
                      <AutofillSafeInput
                        fieldName={`order-conflict-note-${item.id}`}
                        value={draft.note}
                        aria-label="冲突处置说明"
                        placeholder="填写原始单据、班次或转交信息"
                        onUserChange={(value) =>
                          setDrafts((current) => ({
                            ...current,
                            [item.id]: { ...draft, note: value },
                          }))
                        }
                      />
                      <AutofillSafeInput
                        fieldName={`order-conflict-authorization-${item.id}`}
                        value={draft.managerPin}
                        aria-label="经理密码或演示授权码"
                        placeholder="经理密码 / 演示授权码"
                        type="password"
                        sensitive
                        onUserChange={(value) =>
                          setDrafts((current) => ({
                            ...current,
                            [item.id]: { ...draft, managerPin: value },
                          }))
                        }
                      />
                      <button
                        className="button primary"
                        type="submit"
                        disabled={
                          orderConflictDispositionRecorded ||
                          !conflictResolutionReady ||
                          draft.note.trim().length < 2 ||
                          draft.managerPin.length < 6 ||
                          conflictMutation.isPending
                        }
                        title={
                          conflictResolutionReady
                            ? draft.disposition === "KEEP_ISOLATED"
                              ? "经理确认原记录继续隔离"
                              : "经理确认隔离并转交账务复核"
                            : "云端同步健康检查通过后才可处置"
                        }
                      >
                        <ShieldCheck size={16} />
                        {orderConflictDispositionRecorded
                          ? item.reasonCode === "ISOLATION_CONFIRMED"
                            ? "已确认保留隔离"
                            : "已转交，等待账务复核"
                          : conflictResolutionReady
                            ? draft.disposition === "KEEP_ISOLATED"
                              ? "确认并保留隔离"
                              : "确认隔离并转交"
                            : "处置不可用：服务未恢复"}
                      </button>
                    </form>
                  )}
                </div>
              </article>
            );
          })
        ) : (
          <div className="empty-state">当前没有待人工确认事项</div>
        )}
      </section>
    </>
  );
}

function serviceRequestActorLabel(request: ServiceRequestRecord): string {
  if (request.status === "PENDING") return "尚未接单";
  return request.assignedActorDisplayName ?? request.assignedActorId ?? "负责人未记录";
}

export function ServiceRequestTimeline({
  request,
}: {
  request: ServiceRequestRecord;
}): React.JSX.Element {
  const actor = serviceRequestActorLabel(request);
  return (
    <ol className="task-timeline" aria-label="处理时间线">
      <li>
        <span>桌边发起</span>
        <time dateTime={request.requestedAt}>
          {new Date(request.requestedAt).toLocaleString("zh-CN", { hour12: false })}
        </time>
      </li>
      {request.acknowledgedAt ? (
        <li>
          <span>{actor} 接单</span>
          <time dateTime={request.acknowledgedAt}>
            {new Date(request.acknowledgedAt).toLocaleString("zh-CN", { hour12: false })}
          </time>
        </li>
      ) : null}
      {request.completedAt ? (
        <li>
          <span>{actor} 完成</span>
          <time dateTime={request.completedAt}>
            {new Date(request.completedAt).toLocaleString("zh-CN", { hour12: false })}
          </time>
        </li>
      ) : null}
      {request.status === "FAILED" ? (
        <li>
          <span>处理失败，等待重新安排</span>
          <time dateTime={request.updatedAt}>
            {new Date(request.updatedAt).toLocaleString("zh-CN", { hour12: false })}
          </time>
        </li>
      ) : null}
      <li className="task-identity" title={request.eventId}>
        <span>任务编号</span>
        <code>{request.eventId}</code>
      </li>
    </ol>
  );
}

export function taskDestination(
  request: Pick<ServiceRequestRecord, "eventId" | "tableNodeId">,
  role: OperatorRole,
  currentSearch = "",
): string {
  const search = new URLSearchParams(currentSearch);
  if (role === "WAITER") {
    search.set("eventId", request.eventId);
    return `/staff?${search.toString()}`;
  }
  search.set("tableNodeId", request.tableNodeId);
  return `/frontdesk?${search.toString()}`;
}

export function TaskDesk({ operator }: { operator: OperatorIdentity }): React.JSX.Element {
  const location = useLocation();
  const taskQuery = useQuery({
    queryKey: ["service-requests"],
    queryFn: () => listServiceRequests(),
    refetchInterval: 1_000,
  });
  const tablesQuery = useQuery({
    queryKey: ["tables"],
    queryFn: listTables,
    refetchInterval: 1_000,
  });
  const items = taskQuery.data ?? [];
  return (
    <>
      <PageHeader
        eyebrow="现场协作"
        title="服务待办"
        action={<span className="demo-pill">{items.length} 项</span>}
      />
      <LongSessionAlertPanel tables={tablesQuery.data ?? []} operator={operator} />
      <section className="task-list">
        {taskQuery.isError ? <div className="error-band">待办读取失败</div> : null}
        {items.length === 0 ? (
          <div className="empty-state">当前没有桌边请求</div>
        ) : (
          items.map((item) => (
            <article className="task-row" key={item.eventId}>
              <span className={`task-icon ${item.type.toLowerCase()}`}>
                {item.type === "SERVICE" ? <BellRing /> : <Banknote />}
              </span>
              <div className="task-content">
                <strong>
                  {item.tableNodeId.replace("table_node_", "")} 号台 ·{" "}
                  {item.type === "SERVICE" ? "呼叫服务" : "请求结账"}
                </strong>
                <span className="task-assignee">负责人：{serviceRequestActorLabel(item)}</span>
                <ServiceRequestTimeline request={item} />
              </div>
              <div className="task-actions">
                <span className={`task-status ${item.status.toLowerCase()}`}>
                  {serviceStatusLabels[item.status]}
                </span>
                <NavLink
                  className="task-open-link"
                  to={taskDestination(item, operator.role, location.search)}
                >
                  {operator.role === "WAITER" ? "去处理" : "打开这张球台"}
                  <ChevronRight size={15} aria-hidden="true" />
                </NavLink>
              </div>
            </article>
          ))
        )}
      </section>
    </>
  );
}

const serviceStatusLabels = {
  PENDING: "待接单",
  ACKNOWLEDGED: "处理中",
  COMPLETED: "已完成",
  FAILED: "处理失败",
} as const;

function StaffDesk({ operator }: { operator: OperatorIdentity }): React.JSX.Element {
  const queryClient = useQueryClient();
  const location = useLocation();
  const focusedEventId = new URLSearchParams(location.search).get("eventId");
  const taskQuery = useQuery({
    queryKey: ["service-requests", "waiter"],
    queryFn: () => listServiceRequests("WAITER"),
    refetchInterval: 1_000,
  });
  const transition = useMutation({
    mutationFn: async (input: { eventId: string; action: "ACKNOWLEDGE" | "COMPLETE" }) =>
      input.action === "ACKNOWLEDGE"
        ? acknowledgeServiceRequest(input.eventId, crypto.randomUUID())
        : completeServiceRequest(input.eventId, crypto.randomUUID()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["service-requests"] });
    },
  });
  const items = (taskQuery.data ?? [])
    .filter((item) => item.status !== "COMPLETED")
    .sort((left, right) => {
      if (left.eventId === focusedEventId) return -1;
      if (right.eventId === focusedEventId) return 1;
      return right.requestedAt.localeCompare(left.requestedAt);
    });

  return (
    <>
      <PageHeader
        eyebrow="移动现场工作台"
        title="服务员待办"
        action={
          <span className="demo-pill">
            {operator.displayName} · {operator.role}
          </span>
        }
      />
      <section className="staff-summary" aria-label="服务待办概况">
        <div>
          <span>待接单</span>
          <strong>{items.filter((item) => item.status === "PENDING").length}</strong>
        </div>
        <div>
          <span>处理中</span>
          <strong>{items.filter((item) => item.status === "ACKNOWLEDGED").length}</strong>
        </div>
        <div>
          <span>本端身份</span>
          <strong>{operator.displayName}</strong>
        </div>
      </section>
      {taskQuery.isError ? (
        <div className="error-band">服务待办读取失败，请检查门店网络</div>
      ) : null}
      {transition.error instanceof Error ? (
        <div className="error-band">操作未完成：{transition.error.message}</div>
      ) : null}
      <section className="staff-task-list" aria-live="polite">
        {items.length === 0 ? (
          <div className="empty-state">当前没有未完成的现场待办</div>
        ) : (
          items.map((item) => {
            const assignedToCurrent = item.assignedActorId === operator.id;
            return (
              <article
                className={`staff-task ${item.type.toLowerCase()}${item.eventId === focusedEventId ? " is-focused" : ""}`}
                key={item.eventId}
                aria-current={item.eventId === focusedEventId ? "true" : undefined}
              >
                <div className="staff-task-heading">
                  <span className={`task-icon ${item.type.toLowerCase()}`}>
                    {item.type === "SERVICE" ? <BellRing /> : <Banknote />}
                  </span>
                  <div>
                    <strong>{item.tableNodeId.replace("table_node_", "")} 号台</strong>
                    <small>{item.type === "SERVICE" ? "呼叫服务" : "请求结账"}</small>
                  </div>
                  <span className={`task-status ${item.status.toLowerCase()}`}>
                    {serviceStatusLabels[item.status]}
                  </span>
                </div>
                <div className="staff-task-meta">
                  <span>
                    发起 {new Date(item.requestedAt).toLocaleString("zh-CN", { hour12: false })}
                  </span>
                  {item.acknowledgedAt ? (
                    <span>
                      接单{" "}
                      {new Date(item.acknowledgedAt).toLocaleString("zh-CN", { hour12: false })}
                    </span>
                  ) : null}
                  <span className="task-identity" title={item.eventId}>
                    任务编号 {item.eventId}
                  </span>
                </div>
                {item.status === "PENDING" ? (
                  <button
                    className="button primary"
                    type="button"
                    disabled={transition.isPending}
                    onClick={() =>
                      transition.mutate({ eventId: item.eventId, action: "ACKNOWLEDGE" })
                    }
                  >
                    <UserRoundCheck size={17} /> 接单
                  </button>
                ) : (
                  <button
                    className="button primary"
                    type="button"
                    disabled={!assignedToCurrent || transition.isPending}
                    title={assignedToCurrent ? "确认现场事项已经完成" : "该待办已由其他员工接单"}
                    onClick={() => transition.mutate({ eventId: item.eventId, action: "COMPLETE" })}
                  >
                    <Check size={17} /> {assignedToCurrent ? "完成待办" : "其他员工处理中"}
                  </button>
                )}
              </article>
            );
          })
        )}
      </section>
    </>
  );
}

export function OwnerBusinessMetrics({
  summary,
}: {
  summary: OwnerBusinessSummary | undefined;
}): React.JSX.Element {
  if (!summary) {
    return (
      <section className="owner-metrics" aria-label="经营收益摘要" aria-busy="true">
        <div>
          <span>商品成本</span>
          <strong>读取中</strong>
          <small>正在汇总各订单成交时记录的商品成本</small>
        </div>
        <div>
          <span>商品毛利</span>
          <strong>读取中</strong>
          <small>不会用当前成本冒充历史成本</small>
        </div>
        <div>
          <span>提成应付</span>
          <strong>读取中</strong>
          <small>正在汇总助教服务记录</small>
        </div>
        <div>
          <span>退款损失</span>
          <strong>读取中</strong>
          <small>正在核对退款流水</small>
        </div>
      </section>
    );
  }
  const incompleteCost = summary.productCostKnownQuantity < summary.productQuantity;
  return (
    <section className="owner-metrics" aria-label="经营收益摘要">
      <div className={incompleteCost ? "needs-attention" : ""}>
        <span>商品成本</span>
        <strong>{formatFee(summary.productCostFen)}</strong>
        <small>
          {summary.productCostKnownQuantity} / {summary.productQuantity} 件已记录成交时成本
        </small>
      </div>
      <div className={incompleteCost ? "needs-attention" : ""}>
        <span>商品毛利（按已记录成本）</span>
        <strong>{formatFee(summary.productGrossProfitFen)}</strong>
        <small>商品销售 {formatFee(summary.productRevenueFen)}</small>
      </div>
      <div>
        <span>提成应付</span>
        <strong>{formatFee(summary.commissionPayableFen)}</strong>
        <small>来自当日助教服务记录</small>
      </div>
      <div className={summary.refundLossFen > 0 ? "needs-attention" : ""}>
        <span>退款损失</span>
        <strong>{formatFee(summary.refundLossFen)}</strong>
        <small>按退款流水汇总</small>
      </div>
      <div
        className={
          summary.lowStockProducts > 0 || summary.outOfStockProducts > 0 ? "needs-attention" : ""
        }
      >
        <span>库存预警</span>
        <strong>{summary.lowStockProducts + summary.outOfStockProducts} 种</strong>
        <small>
          低库存 {summary.lowStockProducts} 种 · 缺货 {summary.outOfStockProducts} 种
        </small>
      </div>
      <div className={summary.cashDifferenceFen ? "needs-attention" : ""}>
        <span>现金差异</span>
        <strong>
          {summary.cashDifferenceFen === null ? "待交班清点" : formatFee(summary.cashDifferenceFen)}
        </strong>
        <small>账面应有 {formatFee(summary.expectedCashFen)}</small>
      </div>
      {incompleteCost ? (
        <details className="owner-cost-detail">
          <summary>查看缺少成交时成本记录的订单明细</summary>
          {summary.missingCostSnapshots.length ? (
            <div className="owner-cost-detail-list">
              {summary.missingCostSnapshots.map((item) => (
                <article key={`${item.orderId}:${item.skuId}`}>
                  <strong>
                    {item.tableNumber} 号台 · {item.productName} × {item.quantity}
                  </strong>
                  <span>订单 {item.orderId}</span>
                  <span>
                    SKU {item.skuId} · 售价 {formatFee(item.unitPriceFen)} ·{" "}
                    {formatTime(item.startedAt)}
                  </span>
                </article>
              ))}
            </div>
          ) : (
            <p>
              历史汇总显示部分商品没有成交时成本记录，但当前明细已不在本机，请按营业日到订单中心核对。
            </p>
          )}
        </details>
      ) : null}
    </section>
  );
}

function OwnerDesk({ operator }: { operator: OperatorIdentity }): React.JSX.Element {
  const [businessDate, setBusinessDate] = useState("");
  const summaryQuery = useQuery({
    queryKey: ["owner-reconciliation", businessDate],
    queryFn: () => getOwnerReconciliationSummary(businessDate || undefined),
    refetchInterval: 5_000,
  });
  const businessSummaryQuery = useQuery({
    queryKey: ["owner-business-summary", businessDate],
    queryFn: () => getOwnerBusinessSummary(businessDate || undefined),
    refetchInterval: 5_000,
  });
  const reviewQuery = useQuery({ queryKey: ["manual-review"], queryFn: listManualReview });
  const tablesQuery = useQuery({
    queryKey: ["tables"],
    queryFn: listTables,
    refetchInterval: 1_000,
  });
  const summary = summaryQuery.data;
  const pendingReview = (reviewQuery.data ?? []).filter((item) => item.status === "PENDING");

  return (
    <>
      <PageHeader
        eyebrow="经营配置与核对后台"
        title="老板 / 店长核账"
        action={
          <>
            <span className="demo-pill">{operator.role} · 演示数据</span>
            <NavLink className="icon-button" to="/system" title="系统与交付">
              <Settings size={18} aria-hidden="true" />
            </NavLink>
          </>
        }
      />
      <LongSessionAlertPanel tables={tablesQuery.data ?? []} operator={operator} />
      <section className="owner-filter">
        <label>
          营业日
          <input
            type="date"
            value={businessDate || summary?.businessDate || ""}
            onChange={(event) => setBusinessDate(event.target.value)}
          />
        </label>
        <span>数据来自门店账本；当前计费与渠道结果仍为演示/人工降级证据。</span>
      </section>
      {summaryQuery.isError ? <div className="error-band">核账摘要读取失败</div> : null}
      {businessSummaryQuery.isError ? (
        <div className="error-band">成本、提成和库存摘要读取失败，请稍后重试</div>
      ) : null}
      <section className="owner-metrics" aria-label="营业核账摘要">
        <div>
          <span>系统应收</span>
          <strong>{formatFee(summary?.receivableFen ?? 0)}</strong>
          <small>{summary?.orders ?? 0} 笔订单</small>
        </div>
        <div>
          <span>已确认实收</span>
          <strong>{formatFee(summary?.settledFen ?? 0)}</strong>
          <small>现金 {formatFee(summary?.cashFen ?? 0)}</small>
        </div>
        <div className={(summary?.pendingReconciliationFen ?? 0) > 0 ? "needs-attention" : ""}>
          <span>待渠道核对</span>
          <strong>{formatFee(summary?.pendingReconciliationFen ?? 0)}</strong>
          <small>人工确认静态码</small>
        </div>
        <div className={(summary?.unexplainedDifferenceFen ?? 0) !== 0 ? "needs-attention" : ""}>
          <span>未解释差异</span>
          <strong>{formatFee(summary?.unexplainedDifferenceFen ?? 0)}</strong>
          <small>冲突 {summary?.conflicts ?? 0} 笔</small>
        </div>
      </section>
      <OwnerBusinessMetrics summary={businessSummaryQuery.data} />
      <section className="owner-breakdown">
        <div>
          <h2>渠道与退款</h2>
          <dl>
            <div>
              <dt>微信静态码</dt>
              <dd>{formatFee(summary?.staticWechatFen ?? 0)}</dd>
            </div>
            <div>
              <dt>支付宝静态码</dt>
              <dd>{formatFee(summary?.staticAlipayFen ?? 0)}</dd>
            </div>
            <div>
              <dt>退款合计</dt>
              <dd>{formatFee(summary?.refundedFen ?? 0)}</dd>
            </div>
            <div>
              <dt>未结订单</dt>
              <dd>{summary?.unsettledOrders ?? 0} 笔</dd>
            </div>
          </dl>
        </div>
        <div>
          <h2>离线与人工事项</h2>
          <dl>
            <div>
              <dt>离线期间订单</dt>
              <dd>{summary?.offlineOrders ?? 0} 笔</dd>
            </div>
            <div>
              <dt>待人工确认</dt>
              <dd>{pendingReview.length} 笔</dd>
            </div>
            <div>
              <dt>核对异常</dt>
              <dd>{formatFee(summary?.exceptionReconciliationFen ?? 0)}</dd>
            </div>
            <div>
              <dt>同步冲突</dt>
              <dd>{summary?.conflicts ?? 0} 笔</dd>
            </div>
          </dl>
        </div>
      </section>
    </>
  );
}

function downloadTextFile(filename: string, content: string, type: string): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function SystemDesk({ operator }: { operator: OperatorIdentity }): React.JSX.Element {
  const queryClient = useQueryClient();
  const componentHealthTitleId = useId();
  const localStorageTitleId = useId();
  const dataDeliveryTitleId = useId();
  const [catalogCsv, setCatalogCsv] = useState("");
  const [catalogFilename, setCatalogFilename] = useState("");
  const [catalogImportKey, setCatalogImportKey] = useState(() => crypto.randomUUID());
  const [fileError, setFileError] = useState<string | null>(null);
  const statusQuery = useQuery({
    queryKey: ["system-status"],
    queryFn: getSystemStatus,
    refetchInterval: 3_000,
  });
  const versionQuery = useQuery({
    queryKey: ["public-version"],
    queryFn: getPublicVersion,
    retry: 1,
    staleTime: 60_000,
  });
  const preview = useMutation({ mutationFn: previewProductCatalog });
  const applyImport = useMutation({
    mutationFn: () => importProductCatalog(catalogCsv, catalogImportKey),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["system-status"] });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({ queryKey: ["connectivity"] });
    },
  });
  const download = useMutation({
    mutationFn: async (kind: "ORDERS" | "DIAGNOSTIC" | "PRODUCT_TEMPLATE") => {
      if (kind === "ORDERS") {
        downloadTextFile("orders.csv", await getOrdersCsv(), "text/csv;charset=utf-8");
      } else if (kind === "DIAGNOSTIC") {
        downloadTextFile(
          "billiard-erp-diagnostic.json",
          await getDiagnosticBundle(),
          "application/json;charset=utf-8",
        );
      } else {
        downloadTextFile(
          "product-catalog-template.csv",
          await getProductCatalogTemplate(),
          "text/csv;charset=utf-8",
        );
      }
    },
  });
  const status = statusQuery.data;
  const importDisabledReason = !catalogCsv
    ? "请先选择商品 CSV"
    : applyImport.data
      ? "当前 CSV 已应用；选择新文件后才能再次导入"
      : preview.isPending
        ? "正在校验 CSV"
        : !preview.data
          ? "请先完成校验预览"
          : preview.data.errors.length > 0
            ? "CSV 仍有校验错误"
            : status?.gateway.mode !== "ONLINE"
              ? "批量导入需要中心账本在线且同步正常"
              : null;

  return (
    <>
      <PageHeader
        eyebrow={`${operator.role} · 门店管理`}
        title="员工管理与系统帮助"
        action={
          <button
            type="button"
            className="icon-button"
            title="刷新系统状态"
            onClick={() => void statusQuery.refetch()}
          >
            <RefreshCw size={18} aria-hidden="true" />
          </button>
        }
      />
      {statusQuery.isError ? (
        <div className="error-band" role="alert">
          系统状态读取失败：门店网关不可达或当前角色无权访问
        </div>
      ) : null}
      <section
        className={`system-health ${status?.overall === "HEALTHY" ? "is-healthy" : "is-degraded"}`}
        aria-live="polite"
      >
        <ServerCog size={22} aria-hidden="true" />
        <div>
          <strong>
            {statusQuery.isPending
              ? "正在核对组件状态"
              : status?.overall === "HEALTHY"
                ? "门店系统运行正常"
                : "部分组件需要关注"}
          </strong>
          <span>
            联网状态：{status?.gateway.mode === "ONLINE" ? "在线" : "正在连接或离线"} · 本地数据：
            {status?.storage.integrity === "OK" ? "正常" : "正在检查"}
          </span>
        </div>
        <span className="evidence-label">演示数据</span>
      </section>

      <details className="system-maintenance-details">
        <summary>
          <ServerCog size={18} aria-hidden="true" /> 项目维护诊断（非日常经营）
        </summary>
        <p className="system-maintenance-note">
          这里供项目维护人员排查同步和版本问题。员工账号管理不需要理解下面的数据。
        </p>
        <BuildProvenanceStamp
          gateway={versionQuery.data}
          loading={versionQuery.isPending}
          unavailable={versionQuery.isError}
        />
        <section className="system-section" aria-labelledby={componentHealthTitleId}>
          <div className="section-heading">
            <div>
              <h2 id={componentHealthTitleId}>版本与健康</h2>
              <p>当前收银端可见组件</p>
            </div>
          </div>
          <table className="component-table">
            <caption className="visually-hidden">组件版本与健康状态</caption>
            <thead>
              <tr className="component-row component-head">
                <th scope="col">组件</th>
                <th scope="col">版本</th>
                <th scope="col">数据结构版本</th>
                <th scope="col">状态</th>
              </tr>
            </thead>
            <tbody>
              {(status?.components ?? []).map((component) => (
                <tr className="component-row" key={component.component}>
                  <th scope="row">{component.component}</th>
                  <td>{component.productVersion}</td>
                  <td>{component.schemaVersion || "-"}</td>
                  <td>
                    <span className={`health-badge is-${component.health.toLowerCase()}`}>
                      {component.health}
                    </span>
                  </td>
                </tr>
              ))}
              {statusQuery.isPending ? (
                <tr>
                  <td className="loading-state" colSpan={4}>
                    正在读取版本信息…
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </section>

        <section className="system-section" aria-labelledby={localStorageTitleId}>
          <div className="section-heading">
            <div>
              <h2 id={localStorageTitleId}>本地可靠性</h2>
              <p>本地订单、待同步记录与人工事项</p>
            </div>
          </div>
          <div className="system-metrics">
            <div>
              <Database size={18} aria-hidden="true" />
              <span>订单</span>
              <strong>{status?.storage.orders ?? 0}</strong>
            </div>
            <div>
              <HardDrive size={18} aria-hidden="true" />
              <span>待同步记录</span>
              <strong>{status?.storage.pendingOutbox ?? 0}</strong>
            </div>
            <div>
              <AlertTriangle size={18} aria-hidden="true" />
              <span>失败/冲突</span>
              <strong>{status?.storage.failedOutbox ?? 0}</strong>
            </div>
            <div>
              <FileCheck2 size={18} aria-hidden="true" />
              <span>待人工确认</span>
              <strong>{status?.storage.pendingManualReview ?? 0}</strong>
            </div>
          </div>
        </section>
      </details>

      <DeferredRoute>
        <StaffManagementDesk operator={operator} />
      </DeferredRoute>

      <details className="system-maintenance-details system-batch-tools">
        <summary>
          <FileUp size={18} aria-hidden="true" /> 导出与批量工具（非日常经营）
        </summary>
        <p className="system-maintenance-note">
          日常商品新增、修改、入库和盘点请使用“商品库存”页面；这里仅供项目维护或确需批量处理时使用。
        </p>
        <section className="system-section delivery-section" aria-labelledby={dataDeliveryTitleId}>
          <div className="section-heading">
            <div>
              <h2 id={dataDeliveryTitleId}>资料导出与批量商品工具</h2>
              <p>订单资料、脱敏诊断和可选批量导入</p>
            </div>
          </div>
          <div className="delivery-actions">
            <button
              className="button secondary"
              type="button"
              onClick={() => download.mutate("ORDERS")}
              disabled={download.isPending}
            >
              <Download size={17} aria-hidden="true" />
              导出订单 CSV
            </button>
            <button
              className="button secondary"
              type="button"
              onClick={() => download.mutate("DIAGNOSTIC")}
              disabled={download.isPending}
            >
              <Download size={17} aria-hidden="true" />
              下载脱敏诊断
            </button>
            <button
              className="button secondary"
              type="button"
              onClick={() => download.mutate("PRODUCT_TEMPLATE")}
              disabled={download.isPending}
            >
              <Download size={17} aria-hidden="true" />
              商品 CSV 模板
            </button>
          </div>
          {download.error instanceof Error ? (
            <div className="error-band" role="alert">
              文件生成失败：{download.error.message}
            </div>
          ) : null}

          <div className="catalog-import">
            <div className="catalog-file-row">
              <label className="button secondary file-button">
                <FileUp size={17} aria-hidden="true" />
                选择商品 CSV
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    preview.reset();
                    applyImport.reset();
                    setFileError(null);
                    if (!file) return;
                    setCatalogImportKey(crypto.randomUUID());
                    event.currentTarget.value = "";
                    if (file.size > 256 * 1024) {
                      setCatalogCsv("");
                      setCatalogFilename(file.name);
                      setFileError("CSV 文件不能超过 256 KiB");
                      return;
                    }
                    void file
                      .text()
                      .then((content) => {
                        setCatalogCsv(content);
                        setCatalogFilename(file.name);
                      })
                      .catch(() => {
                        setCatalogCsv("");
                        setCatalogFilename(file.name);
                        setFileError("无法读取所选 CSV 文件");
                      });
                  }}
                />
              </label>
              <span className="selected-file">{catalogFilename || "尚未选择文件"}</span>
              <button
                className="button secondary"
                type="button"
                onClick={() => preview.mutate(catalogCsv)}
                disabled={!catalogCsv || preview.isPending}
              >
                <FileCheck2 size={17} aria-hidden="true" />
                校验预览
              </button>
              <button
                className="button primary"
                type="button"
                title={importDisabledReason ?? "应用商品目录"}
                onClick={() => applyImport.mutate()}
                disabled={Boolean(importDisabledReason) || applyImport.isPending}
              >
                <FileUp size={17} aria-hidden="true" />
                应用导入
              </button>
            </div>
            {fileError ? (
              <div className="error-band" role="alert">
                {fileError}
              </div>
            ) : null}
            {preview.data ? (
              preview.data.errors.length > 0 ? (
                <div className="csv-errors" role="alert">
                  <strong>CSV 校验未通过</strong>
                  <ul>
                    {preview.data.errors.slice(0, 8).map((error) => (
                      <li key={[error.line, error.field, error.message].join("-")}>
                        第 {error.line || "-"} 行 · {error.field}：{error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="csv-preview" aria-live="polite">
                  <strong>
                    校验通过 · {preview.data.rows.length} 行 · {preview.data.fingerprint}
                  </strong>
                  <div className="preview-list">
                    {preview.data.rows.slice(0, 6).map((row) => (
                      <span key={row.skuId}>
                        {row.skuId} · {row.name} · {formatFee(row.unitPriceFen)} ·{" "}
                        {row.enabled ? "启用" : "停用"}
                      </span>
                    ))}
                  </div>
                </div>
              )
            ) : null}
            {applyImport.data ? (
              <output className="success-band">
                <Check size={18} aria-hidden="true" />
                已应用 {applyImport.data.imported} 项，未变化 {applyImport.data.unchanged}{" "}
                项；结果已经保存。维护人员可在诊断信息中查看配置版本和数据来源。
              </output>
            ) : null}
            {applyImport.error instanceof Error ? (
              <div className="error-band" role="alert">
                商品导入失败：{applyImport.error.message}
              </div>
            ) : null}
            {importDisabledReason && catalogCsv && preview.data?.errors.length === 0 ? (
              <p className="disabled-reason">当前不可应用：{importDisabledReason}</p>
            ) : null}
          </div>
        </section>
      </details>
    </>
  );
}

export function App(): React.JSX.Element {
  const queryClient = useQueryClient();
  const session = useQuery({
    queryKey: ["operator-session"],
    queryFn: getOperatorSession,
    retry: false,
    refetchInterval: isNativeRuntime() ? 2_000 : false,
  });
  useEffect(() => {
    if (session.data?.operator) setOperatorRequestIdentity(session.data.operator);
  }, [session.data?.operator]);
  useEffect(() => {
    let handle: { remove(): Promise<void> } | null = null;
    void listenForNativeResume(() => void session.refetch()).then((next) => {
      handle = next;
    });
    return () => void handle?.remove();
  }, [session.refetch]);
  if (session.isPending) {
    return (
      <>
        <NativeRuntimeNotice />
        <main className="auth-screen">
          <div className="auth-loading" aria-live="polite">
            <RefreshCw className="spin" size={20} />
            正在验证员工会话
          </div>
        </main>
      </>
    );
  }
  if (session.error instanceof ApiRequestError && session.error.code === "AUTH_REQUIRED") {
    return (
      <>
        <NativeRuntimeNotice />
        <LoginScreen
          onAuthenticated={() => {
            void queryClient.invalidateQueries({ queryKey: ["operator-session"] });
          }}
        />
      </>
    );
  }
  if (!session.data) {
    return (
      <>
        <NativeRuntimeNotice />
        <main className="auth-screen">
          <div className="auth-panel auth-unreachable" role="alert">
            <WifiOff size={22} />
            <strong>门店网关不可达</strong>
            <span>
              {session.error instanceof Error ? session.error.message : "无法验证员工会话"}
            </span>
            <button
              className="button secondary"
              type="button"
              onClick={() => void session.refetch()}
            >
              <RefreshCw size={17} />
              重试
            </button>
          </div>
        </main>
      </>
    );
  }
  return (
    <>
      <NativeRuntimeNotice />
      <Shell
        operator={session.data.operator}
        authenticationEvidence={session.data.evidence}
        sessionContext={session.data.session}
      />
    </>
  );
}
